import { useLayoutEffect } from 'react';

export const DEFAULT_LOCALE = 'en';
export const SUPPORTED_LOCALES = ['en', 'zh-CN'];

// The application historically used Chinese source strings. Keeping those
// strings as the canonical source lets existing server messages and saved
// labels remain compatible while the UI can default to English.
const EN = {
  'Hinomi 招聘工作台': 'Hinomi Recruiting Workspace',
  '工作台': 'Dashboard',
  '候选人': 'Candidates',
  '候选人档案': 'Candidate Profile',
  '导入与评估': 'Import & Assess',
  '导入': 'Import',
  '岗位管理': 'Jobs',
  '部门管理': 'Departments',
  '人工校准': 'Human Calibration',
  '人才标准': 'Talent Standards',
  '人才标准库': 'Talent Standards Library',
  '账户与权限': 'Accounts & Access',
  '模型与处理': 'Models & Processing',
  '审计记录': 'Audit Log',
  'AI 诊断语言包尚未生成': 'AI assessment language pack is not ready',
  '当前语言的 AI 诊断尚未生成，暂时显示原语言内容。': 'The AI assessment is not yet available in the selected language. The original language is shown for now.',
  '诊断内容翻译': 'Assessment Localization',
  '招聘工作台': 'Recruiting Workspace',
  '主导航': 'Main navigation',
  '系统': 'System',
  '负责岗位': 'Assigned jobs',
  '证据流水线': 'Evidence pipeline',
  '解析服务可用': 'Parsing service available',
  '打开导航': 'Open navigation',
  '关闭导航': 'Close navigation',
  '演示数据': 'Demo data',
  '任务': 'Tasks',
  '当前账户': 'Current account',
  '修改密码': 'Change password',
  '退出登录': 'Sign out',
  '未登录': 'Not signed in',
  '个人资料': 'Profile',
  '个人资料设置': 'Profile Settings',
  '管理个人显示名称、头像及默认界面语言。': 'Manage your display name, avatar, and default UI language.',
  '显示姓名 *': 'Display Name *',
  '请输入姓名': 'Enter your name',
  '界面默认语言': 'Default UI Language',
  'English (默认英文)': 'English (Default)',
  '简体中文': 'Simplified Chinese',
  '更换头像': 'Change Avatar',
  '上传头像': 'Upload Avatar',
  '清除头像': 'Remove Avatar',
  '支持 JPG、PNG、WEBP，最大 10MB': 'Supports JPG, PNG, WEBP up to 10MB',
  '保存资料': 'Save Profile',
  '个人资料已更新': 'Profile updated',
  '无法更新个人资料': 'Unable to update profile',
  '取消评估': 'Cancel Assessment',
  '已取消 AI 评估': 'AI assessment cancelled',
  '取消任务': 'Cancel Task',
  '正在取消': 'Cancelling',
  '已取消': 'Cancelled',
  '审核已由用户取消': 'Assessment cancelled by user',
  '已从队列取消': 'Cancelled from queue',
  '任务已取消': 'Task cancelled',
  '处理中心': 'Processing Center',
  '后台任务': 'Background Tasks',
  '处理中': 'Processing',
  '已排队': 'Queued',
  '失败': 'Failed',
  '完成': 'Completed',
  '已完成': 'Completed',
  '已评分': 'Scored',
  '招聘中': 'Active',
  '已暂停': 'Paused',
  '待开始评估': 'Ready for assessment',
  '处理失败': 'Processing failed',
  '待匹配': 'Unmatched',
  '已解析': 'Parsed',
  '仅链接': 'Link only',
  '仅供人工查看': 'Human review only',
  '当前没有处理任务': 'No processing tasks',
  '批量任务': 'Batch task',
  '正在重新排队': 'Re-queuing',
  '重试任务': 'Retry task',
  '任务已重新排队': 'Task re-queued',
  '关闭': 'Close',
  '取消': 'Cancel',
  '保存': 'Save',
  '删除': 'Delete',
  '编辑': 'Edit',
  '添加': 'Add',
  '创建': 'Create',
  '确认': 'Confirm',
  '重置': 'Reset',
  '搜索': 'Search',
  '启用': 'Enabled',
  '停用': 'Disabled',
  '已配置': 'Configured',
  '未配置': 'Not configured',
  '未知': 'Unknown',
  '暂无': 'None',
  '查看': 'View',
  '详情': 'Details',
  '返回': 'Back',
  '下一步': 'Next',
  '上一步': 'Back',
  '全部': 'All',
  '当前': 'Current',
  '状态': 'Status',
  '名称': 'Name',
  '说明': 'Description',
  '类型': 'Type',
  '操作': 'Actions',
  '时间': 'Time',
  '模型': 'Model',
  '岗位': 'Job',
  '部门': 'Department',
  '角色': 'Role',
  '管理员': 'Administrator',
  '招聘官': 'Hiring Manager',
  '人事': 'HR',

  '招聘全局总览': 'Recruiting Overview',
  '招聘运营工作台': 'Recruiting Operations',
  '我的招聘待办': 'My Recruiting Tasks',
  '导入候选人': 'Import Candidates',
  '候选人工作流': 'Candidate Workflow',
  '我的候选人': 'My Candidates',
  'AI 建议与人工招聘阶段分开管理': 'Manage AI recommendations separately from human recruiting stages',
  '候选人 / 岗位': 'Candidate / Job',
  'AI 排序分': 'AI Score',
  'AI 建议': 'AI Recommendation',
  '人工招聘阶段': 'Human Stage',
  '证据与风险': 'Evidence & Risk',
  '快捷操作': 'Quick Actions',
  '岗位筛选': 'Job filter',
  '全部岗位': 'All Jobs',
  '招聘阶段筛选': 'Recruiting stage filter',
  '需复核': 'Needs Review',
  '备选': 'Shortlist',
  '推荐面试': 'Recommend Interview',
  '待约面': 'Scheduling',
  '待面试': 'Interview Scheduled',
  '不匹配': 'Not a Match',
  '继续复核': 'Continue Review',
  '低优先级': 'Low Priority',
  '强推荐面试': 'Strongly Recommend Interview',
  '建议发 Offer': 'Recommend Offer',
  '已发 Offer': 'Offer Sent',
  '接受 Offer': 'Offer Accepted',
  '拒绝 Offer': 'Offer Declined',
  '已入职': 'Hired',
  '设为备选': 'Add to Shortlist',
  '设为不匹配': 'Mark Not a Match',
  '评估未完成项': 'Assess Incomplete',
  '开始处理': 'Start Processing',
  '人工复核': 'Human Review',
  '材料已归档，但解析或模型评估尚未完成。': 'Files are archived, but parsing or model assessment is not complete.',
  '搜索姓名、岗位、风险或摘要': 'Search name, job, risk, or summary',
  '按 AI 分数': 'Sort by AI score',
  '按阶段更新时间': 'Sort by stage update',
  '按置信度': 'Sort by confidence',
  '按风险': 'Sort by risk',
  '取消选择': 'Clear selection',
  '批量设为不匹配': 'Mark Candidates as Not a Match',
  '批量删除候选人': 'Delete Candidates in Bulk',
  '确认执行': 'Confirm Action',
  '该操作会更新所选候选人的人工招聘阶段，但不会改变 AI 原始评分。': 'This updates the selected candidates’ human recruiting stage without changing their original AI scores.',
  '将批量删除所选候选人、材料、评估、面试与通知记录，且无法撤销。': 'This permanently deletes the selected candidates, files, assessments, interviews, and notification records.',
  '批量操作会记录到每位候选人的流程历史和审计日志。': 'The batch action is recorded in every candidate’s workflow history and audit log.',
  '请选择 1 至 100 位候选人。': 'Select between 1 and 100 candidates.',
  '部分候选人不存在，请刷新列表后重试。': 'Some candidates no longer exist. Refresh the list and try again.',
  '全选': 'Select all',
  '删除候选人': 'Delete candidate',
  '查看候选人': 'View candidate',
  '没有符合条件的候选人': 'No candidates match the filters',
  '调整岗位或阶段筛选后重试。': 'Adjust the job or stage filters and try again.',
  'AI 建议不会自动改变人工招聘阶段': 'AI recommendations never change the human recruiting stage automatically',
  '确认删除': 'Confirm deletion',
  '确认删除全部候选人数据': 'Confirm deletion of all candidate data',
  '岗位和其他候选人不会受影响。': 'The job and other candidates will not be affected.',

  '备选 / 推荐': 'Shortlist / Recommended',
  '约面 / 待面试': 'Scheduling / Interview',
  '等待招聘官判断': 'Waiting for hiring review',
  '优先查看': 'Priority Candidates',
  '综合分、证据置信度与人工调整后的当前排序': 'Current ranking by score, evidence confidence, and human adjustments',
  '完整排名': 'Full ranking',
  '招聘阶段分布': 'Recruiting Stage Distribution',
  'AI 建议与人工招聘阶段相互独立': 'AI recommendations and human stages are independent',
  '需要判断的事项': 'Items Requiring Review',
  '按风险强度和置信度排序': 'Sorted by risk severity and confidence',
  '复核队列已清空': 'The review queue is clear',
  '证据待复核': 'Evidence requires review',
  '潜力信号': 'Potential Signals',
  '识别学习速度，不被当前年资掩盖': 'Identify learning velocity beyond current seniority',
  '查看优秀人才特征库': 'View high-performer patterns',
  '处理流水线': 'Processing Pipeline',
  '当前处理服务空闲': 'Processing service is idle',
  '候选人处理': 'Candidate processing',
  '系统已就绪': 'System ready',
  '新导入材料将进入可恢复任务队列': 'New files will enter the recoverable task queue',
  '归档': 'Archive',
  '文本层': 'Text Layer',
  '渲染': 'Render',
  '简历复核': 'Resume Review',
  '评分': 'Scoring',
  '潜力待评估': 'Potential pending',

  '岗位配置': 'Job Configuration',
  '岗位证据门槛与评分权重决定候选人如何排序': 'Evidence requirements and scoring weights determine candidate ranking',
  '新建岗位': 'New Job',
  '添加岗位': 'Add Job',
  '编辑岗位': 'Edit Job',
  '基本信息': 'Basic Information',
  '要求与技能': 'Requirements & Skills',
  '团队与目标': 'Team & Goals',
  '评分权重': 'Scoring Weights',
  '岗位基本信息': 'Job Basics',
  '用于定义筛选范围与候选人预期': 'Defines the screening scope and candidate expectations',
  '岗位名称 *': 'Job title *',
  '岗位名称': 'Job title',
  '所属部门 *': 'Department *',
  '所属部门': 'Department',
  '请选择部门': 'Select a department',
  '岗位等级': 'Job level',
  '工作地点': 'Location',
  '最低薪资': 'Minimum salary',
  '最高薪资': 'Maximum salary',
  '币种': 'Currency',
  '岗位 JD': 'Job Description',
  '上传 JD 文件': 'Upload JD file',
  '支持 PDF、DOCX、TXT 与图片；导入后会自动整理为标准结构': 'Supports PDF, DOCX, TXT, and images; imported content is normalized automatically',
  '硬门槛': 'Hard Requirements',
  '不满足时强标记风险，但不自动淘汰': 'Flag unmet requirements as risks without automatic rejection',
  '每行一项': 'One item per line',
  '技能优先级': 'Skill Priorities',
  '描述该技能需要覆盖的业务领域或经验场景，不要求填写量化最低证据': 'Describe the business domains or experience scenarios required; quantified evidence is optional',
  '添加技能': 'Add Skill',
  '技能': 'Skills',
  '权重': 'Weight',
  '领域经验要求': 'Domain Experience Requirements',
  '技能名称': 'Skill name',
  '核心': 'Core',
  '加分': 'Bonus',
  '删除技能': 'Delete skill',
  '尚未添加技能优先级': 'No skill priorities added',
  '技能权重合计': 'Total skill weight',
  '分配完整': 'Fully allocated',
  '建议合计为 100%': 'Recommended total: 100%',
  '团队上下文': 'Team Context',
  '让评分考虑真实补位目标，而非只匹配 JD 关键词': 'Score against real team needs, not just JD keywords',
  '现有团队角色': 'Current team roles',
  '团队能力缺口': 'Team capability gaps',
  '汇报对象与汇报关系': 'Reporting line',
  '主要协作对象': 'Key collaborators',
  '当前业务阶段': 'Current business stage',
  '本次招聘补位目标': 'Hiring objective',
  '岗位评分权重': 'Job Scoring Weights',
  '每个岗位可独立维护评分指标；普通岗位不建议把语言作为评分项': 'Each job can define its own criteria; language is usually not a scoring dimension',
  '添加指标': 'Add Criterion',
  '权重分配完整，可以保存。': 'Weights are complete and ready to save.',
  '所有维度权重必须合计为 100%。': 'All dimension weights must total 100%.',
  '有未保存修改': 'Unsaved changes',
  '尚未保存的新岗位': 'Unsaved new job',
  '保存配置': 'Save Configuration',

  '批量导入': 'Batch Import',
  '导入简历': 'Import Resumes',
  '简历导入与评估': 'Resume Import & Assessment',
  '选择归档岗位并上传候选人材料': 'Choose a job and upload candidate files',
  '归档岗位': 'Target job',
  '查看候选人库': 'View Candidates',
  '拖放简历或点击选择文件': 'Drop resumes here or click to select files',
  '支持 PDF、DOCX、图片与文本文件': 'Supports PDF, DOCX, images, and text files',
  '选择文件': 'Choose Files',
  '移除文件': 'Remove file',
  '清空': 'Clear',
  '归档预览': 'Archive Preview',
  '审核模型尚未就绪': 'Assessment model is not ready',
  '配置审核模型': 'Configure Assessment Model',
  'AI 处理暂不可用': 'AI Processing Is Unavailable',
  '配置 AI Provider': 'Configure AI Provider',
  '请先在“模型与处理”中为审核模型选择已启用的 Provider 和 Model，并配置 API Base URL 与 API Key。当前仍可导入并归档简历，但不会启动 AI 处理。': 'Select an enabled provider and model for the Assessment Model under Models & Processing, then configure the API Base URL and API key. You can still import and archive resumes, but AI processing will not start.',
  '完成 AI Provider 配置后才可启用': 'Available after the AI provider is configured',
  '请先配置可用的 AI 审核 Provider 和模型': 'Configure an available AI assessment provider and model first',
  '自动开始 AI 评估': 'Start AI assessment automatically',
  '导入并开始评估': 'Import & Start Assessment',
  '仅导入归档': 'Archive Only',
  '人工确认姓名': 'Confirm Candidate Name',
  '仅文件名无法识别候选人时进入这里，确认后可直接继续评估': 'Only files with unrecognized candidate names appear here',
  '没有待匹配资产': 'No unmatched files',
  '所有已导入文件都已归档到候选人。': 'All imported files are assigned to candidates.',
  '输入候选人姓名': 'Enter candidate name',
  '归档并评估': 'Archive & Assess',
  '确认归档': 'Confirm Archive',
  '待处理': 'Pending',

  'AI 运行设置': 'AI Runtime Settings',
  '管理员设置': 'Admin Settings',
  'Provider、模型分工、上下文边界与运行追踪': 'Providers, model roles, context boundaries, and runtime tracing',
  '保存模型与策略': 'Save Models & Policies',
  '新增 Custom': 'Add Custom',
  '显示名称': 'Display name',
  '输入 API Key': 'Enter API key',
  '保存 Provider': 'Save Provider',
  '测试连接': 'Test Connection',
  '删除 Provider': 'Delete provider',
  '连接验证通过': 'Connection verified',
  '连接失败': 'Connection failed',
  '模型目录': 'Model Catalog',
  '搜索 Model ID': 'Search Model ID',
  '从 Provider 更新模型': 'Refresh models from provider',
  '添加模型': 'Add Model',
  '编辑模型': 'Edit Model',
  '删除模型': 'Delete Model',
  '模型分工': 'Model Roles',
  '每项任务先固定 Provider，再选择该 Provider 下的模型': 'Choose a provider first, then a model for each task',
  '视觉 OCR': 'Vision OCR',
  '审核模型': 'Assessment Model',
  '翻译模型': 'Translation Model',
  'Fallback 模型': 'Fallback Model',
  '审核输出预算': 'Assessment Output Budget',
  '推理模型的思考也计入此预算': 'A reasoning model bills its thinking against this budget',
  '上下文策略': 'Context Policy',
  '独立 Context Pack、来源清单、去重与分片': 'Isolated context packs, source manifests, deduplication, and chunking',
  '处理策略': 'Processing Policy',
  '新任务的并发、重试与超时': 'Concurrency, retries, and timeouts for new tasks',
  '并发任务数': 'Concurrent tasks',
  '重试次数': 'Retry count',
  '请求超时': 'Request timeout',
  '候选人数据将进入 Langfuse': 'Candidate data will be sent to Langfuse',
  '新增 Custom API Provider': 'Add Custom API Provider',
  '配置一个 OpenAI Chat Completions 兼容端点': 'Configure an OpenAI Chat Completions-compatible endpoint',
  'Provider 名称': 'Provider name',
  '初始模型': 'Initial Model',
  '创建后仍可同步或继续添加模型': 'You can sync or add more models after creation',
  '例如：Internal OpenAI Gateway': 'Example: Internal OpenAI Gateway',
  '输入 Provider API Key': 'Enter provider API key',
  '创建 Provider': 'Create Provider',
  '保存模型': 'Save Model',

  '治理与合规': 'Governance & Compliance',
  '模型设置、岗位变更、评分版本与人工判断均可追溯': 'Trace model settings, job changes, score versions, and human decisions',
  'AI 错误日志': 'AI Error Logs',
  '逐次记录 Provider 请求、Fallback、耗时和上游诊断信息': 'Per-attempt provider, fallback, latency, and upstream diagnostics',
  '搜索模型、候选人或错误码': 'Search model, candidate, or error code',
  '没有 AI 错误记录': 'No AI error records',
  '后续模型请求若失败，会在这里保留逐次诊断信息。': 'Failed model requests will leave per-attempt diagnostics here.',
  '最近活动': 'Recent Activity',
  '当前工作区的重要数据变更': 'Important changes in the current workspace',
  '搜索操作、对象或操作者': 'Search action, object, or actor',
  '暂无审计记录': 'No audit records',

  '组织与权限': 'Organization & Access',
  '账户管理': 'Account Management',
  '管理招聘角色与部门数据范围': 'Manage recruiting roles and department data scope',
  '新建账户': 'New Account',
  '全部账户': 'All Accounts',
  '添加账户': 'Add Account',
  '邮箱用于招聘通知和 Teams 日历邀请': 'Email is used for recruiting notifications and Teams calendar invitations',
  '姓名': 'Name',
  '邮箱': 'Email',
  '账户角色': 'Account role',
  '账户状态': 'Account status',
  '部门可见范围': 'Department Visibility',
  '管理员固定拥有全部部门的系统管理权限': 'Administrators always have system access to all departments',
  '该角色固定拥有全部部门的数据范围': 'This role always has organization-wide department access',
  '部门数据范围': 'Department Data Scope',
  '主要负责部门': 'Primary Departments',
  '用于控制该招聘官可查看和管理的岗位、候选人与简历范围': 'Controls the jobs, candidates, and resumes this hiring manager can view and manage',
  '勾选后可查看该部门当前及未来新增的全部岗位': 'Grants access to all current and future jobs in the department',
  '保存账户': 'Save Account',
  'Microsoft Graph 尚未配置': 'Microsoft Graph is not configured',
  '通知任务暂存': 'Notifications queued',
  '招聘官只能查看和管理已授权负责部门的招聘数据；人事负责全组织招聘运营': 'Hiring managers can only view and manage recruiting data in their assigned departments; HR owns organization-wide recruiting operations',
  '已授权部门内的岗位管理、简历导入、AI 评估、人工复核与面试反馈': 'Job management, resume importing, AI assessment, human review, and interview feedback within assigned departments',
  '全组织岗位管理、简历导入、AI 评估、约面与 Offer 推进': 'Organization-wide job management, resume importing, AI assessment, scheduling, and offer progression',
  '删除账户': 'Delete Account',
  '账户已删除': 'Account deleted',
  '不能删除当前登录账户。': 'You cannot delete the account currently signed in.',
  '必须至少保留一个启用的管理员账户。': 'At least one active administrator account must remain.',
  '密码与登录安全': 'Password & Sign-in Security',
  '管理员可以重置其他账户的密码并撤销其已有会话': 'Administrators can reset another account’s password and revoke its existing sessions',
  '密码已设置': 'Password set',
  '尚未设置密码': 'Password not set',
  '当前登录账户': 'Current signed-in account',
  '请使用页面右上角的“修改密码”入口更新自己的密码；管理员不能在这里绕过当前密码验证。': 'Use “Change password” in the top bar to update your own password; administrators cannot bypass current-password verification here.',
  '新临时密码（留空则不修改）': 'New temporary password (leave blank to keep current)',
  '至少 10 个字符，包含字母和数字': 'At least 10 characters, including letters and numbers',
  '首次登录后必须修改密码': 'Require a password change after first sign-in',
  '重置密码后，该账户在其他设备上的登录会话会立即失效。': 'Resetting the password immediately revokes this account’s sessions on other devices.',

  '部门与数据范围': 'Departments & Data Scope',
  '按部门隔离岗位、候选人与招聘官权限': 'Isolate jobs, candidates, and hiring access by department',
  '新建部门': 'New Department',
  '全部部门': 'All Departments',
  '添加部门': 'Add Department',
  '部门名称': 'Department name',
  '部门说明': 'Department description',
  '启用部门': 'Enable department',
  '保存部门': 'Save Department',

  '人机协作': 'Human-AI Collaboration',
  '记录分歧与最终判断，让评分体系从真实招聘结果中改进': 'Record disagreements and final decisions to improve scoring from real outcomes',
  '查看审计记录': 'View Audit Log',
  '复核队列': 'Review Queue',
  '优先处理低置信度、强风险与高潜力边界样本': 'Prioritize low-confidence, high-risk, and high-potential edge cases',
  '当前筛选项没有待复核候选人': 'No candidates require review',
  '最近校准': 'Recent Calibrations',
  '人工调整不会覆盖原始 AI 版本': 'Human adjustments never overwrite the original AI version',
  '尚未产生校准记录': 'No calibration records yet',
  '编辑校准': 'Edit Calibration',
  '删除校准': 'Delete Calibration',
  '编辑校准评价': 'Edit Calibration Review',
  '人工结论': 'Human Decision',
  '请填写人工结论': 'Enter a human decision',
  '保存修改': 'Save Changes',
  '只有创建者本人可以修改这条评价。': 'Only the person who created this review can edit it.',
  '校准评价已更新': 'Calibration review updated',
  '校准评价已删除': 'Calibration review deleted',
  '校准记录不存在。': 'The calibration record does not exist.',
  '只能修改或删除本人创建的校准记录。': 'You can edit or delete only calibration records you created.',

  '组织人才标准': 'Organization Talent Standards',
  '通用优秀人才特征、常见岗位成功画像与当前候选人观察分层管理': 'Manage high-performer traits, job success profiles, and candidate observations separately',
  '通用特征': 'General Traits',
  '岗位画像': 'Job Profiles',
  '候选人观察': 'Candidate Observations',
  '搜索通用特征或证据信号': 'Search traits or evidence signals',
  '没有匹配的通用特征': 'No matching traits',
  '岗位成功画像尚未配置': 'Job success profile is not configured',
  '评估重点': 'Evaluation Focus',
  '岗位画像基线；具体招聘仍由 JD、等级、团队缺口和目标覆盖': 'Profile baseline; each hire is still governed by the JD, level, team gaps, and goals',
  '潜力候选人观察': 'High-Potential Candidate Observations',
  '潜力与当前胜任度分开呈现，重点关注学习闭环和职责增长': 'Separate potential from current fit, focusing on learning loops and scope growth',
  '当前岗位还没有高潜力候选人': 'No high-potential candidates for this job yet',

  '返回候选人排名': 'Back to Candidate Ranking',
  'AI 参考': 'AI Reference',
  '置信度': 'Confidence',
  '重新翻译': 'Translate Again',
  '生成翻译': 'Generate Translation',
  'AI重审': 'AI Re-review',
  '招聘阶段': 'Recruiting Stage',
  '流程已关闭': 'Workflow closed',
  '打回复核': 'Return to Review',
  '评估总览': 'Assessment Overview',
  '简历文本': 'Resume Text',
  '职业路径': 'Career Path',
  '人工记录': 'Human Records',
  '评估摘要': 'Assessment Summary',
  '审核与评分模型': 'Reviewed and scored by',
  '仅基于简历原文与结构化证据': 'Based only on resume content and structured evidence',
  '待补信息': 'Missing Information',
  '潜力识别': 'Potential Assessment',
  '与当前岗位胜任度分开判断': 'Assessed separately from current job fit',
  '潜力分': 'Potential Score',
  '岗位维度评分': 'Job Dimension Scores',
  '简历证据维度': 'Resume evidence dimensions',
  '风险与面试问题': 'Risks & Interview Questions',
  '提示人工确认，不触发自动拒绝': 'For human verification; never triggers automatic rejection',
  '中文问题': 'Chinese Question',
  '简历基本概况': 'Resume Profile',
  '仅展示简历原文明确支持的信息；悬停可查看抽取证据': 'Only information explicitly supported by the resume; hover to view evidence',
  '教育背景': 'Education',
  '知名公司经历': 'Notable Company Experience',
  '语言能力': 'Languages',
  '联系方式': 'Contact',
  '中文': 'Chinese',
  '英文': 'English',
  '高': 'High',
  '中': 'Medium',
  '低': 'Low',
  '地区与市场经历': 'Regions & Markets',
  '职业资历': 'Career Tenure',
  '无明确记录': 'No explicit record',
  '未识别': 'Not identified',
  '简历原文': 'Original Resume',
  '译文': 'Translation',
  '原文': 'Original',
  '结构化原文': 'Structured Original',
  '结构化简历原文': 'Structured Original Resume',
  '翻译': 'Translation',
  '简历主视图': 'Primary resume view',
  '参考资料': 'References',
  '原始文本': 'Raw Text',
  '原始文件 / PDF': 'Source File / PDF',
  '提取的原始文本': 'Extracted Source Text',
  '翻译版本': 'Translation version',
  '翻译已生成': 'Translation Generated',
  '最新译文已保存，原文仍完整保留。': 'The latest translation is saved and the original remains intact.',
  '当前仅有原文': 'Only the original is available',
  '当前仅有结构化原文': 'Only the structured original is available',
  '点击页面顶部“生成翻译”后，翻译会与结构化原文并列保存。': 'After selecting “Generate Translation” at the top, the translation will be saved alongside the structured original.',
  '作品集（人工查看）': 'Portfolio (Human Review)',
  '仅保留候选人提供的链接或文件，不进入 AI 解析与评分': 'Candidate-provided links and files only; excluded from AI parsing and scoring',
  '历史公司与业务规模': 'Company Background & Business Scale',
  '逐家公司区分行业、业务类型、规模与信息依据，不按公司名气直接加分': 'Review industry, business model, scale, and evidence per company without prestige scoring',
  '职业路径判断': 'Career Path Assessment',
  '只评估职责、项目复杂度和岗位层级，不使用年龄': 'Assess responsibilities, project complexity, and level without using age',
  '需要面试确认': 'Verify in Interview',
  '风险提示不会触发自动淘汰': 'Risk flags never trigger automatic rejection',
  '公平招聘边界': 'Fair Hiring Boundary',
  '招聘阶段记录': 'Recruiting Stage History',
  '人工阶段变更、面试安排和通知任务': 'Human stage changes, interview scheduling, and notifications',
  '尚无招聘阶段变更': 'No recruiting stage changes yet',
  '面试安排': 'Interview Schedule',
  '已确认的面试时间': 'Confirmed interview times',
  '线下面试': 'In-person interview',
  '视频面试': 'Video interview',
  '已安排': 'Scheduled',
  '通知与日历任务': 'Notifications & Calendar Tasks',
  '邮件与 Microsoft Graph 交付状态': 'Email and Microsoft Graph delivery status',
  '待配置': 'Configuration Pending',
  '评分校准': 'Score Calibration',
  '人工调整分与复核备注': 'Human score adjustments and review notes',
  '尚无人工校准记录': 'No human calibration records',
  '评分版本': 'Score Versions',
  '每次 AI 重评与人工调整均保留快照': 'Every AI reassessment and human adjustment retains a snapshot',
  'AI 运行与上下文': 'AI Runs & Context',
  '功能调用、模型、版本和实际上下文范围': 'Function calls, models, versions, and actual context scope',
  '尚无 AI Run': 'No AI runs yet',
  '登记面试': 'Schedule Interview',
  '确认面试安排': 'Confirm Interview',
  '面试时间': 'Interview time',
  '面试方式': 'Interview mode',
  '面试官': 'Interviewers',
  '地点 / 会议说明': 'Location / meeting details',
  'Teams 会议': 'Teams meeting',
  '通知与日历': 'Notifications & Calendar',
  '保存校准': 'Save Calibration',
  '复核备注': 'Review notes',

  // Editing and import workflow
  '二级标题': 'Heading 2',
  '粗体': 'Bold',
  '斜体': 'Italic',
  '无序列表': 'Bulleted list',
  '有序列表': 'Numbered list',
  '引用': 'Quote',
  '标题': 'Heading',
  '重点内容': 'Key content',
  '补充说明': 'Additional details',
  '列表项': 'List item',
  '引用内容': 'Quoted text',
  '标准化排版': 'Normalize Formatting',
  'JD 编辑模式': 'Job Description editor mode',
  '预览': 'Preview',
  '尚未填写 JD 内容': 'No job description content yet',
  '## 岗位职责\n\n- 描述核心职责和预期成果\n\n## 任职要求\n\n- 描述必要经验与能力': '## Responsibilities\n\n- Describe the core responsibilities and expected outcomes\n\n## Requirements\n\n- Describe the required experience and capabilities',
  '批量上传简历': 'Upload Resumes in Bulk',
  'PDF、DOCX、TXT、PNG、JPG、WEBP、TIFF · 单文件不超过 250 MB': 'PDF, DOCX, TXT, PNG, JPG, WEBP, or TIFF · Up to 250 MB per file',
  '上传后按文件名识别候选人，并可立即进入 AI 评估队列': 'Identify candidates from filenames and optionally start AI assessment immediately',
  '会读取文件，并将必要内容发送给已配置的 API Provider': 'Files are read and the required content is sent to the configured API provider',
  '可在候选人页点击“开始评估未完成项”。': 'You can start incomplete assessments from the Candidates page.',
  '可自动归档': 'Auto-archive ready',
  '启动处理后仍会完成文件解析与证据采集，但不会生成 AI 评分。请在 AI 运行设置中为“审核模型”选择 Provider 和 Model。': 'Processing can still parse files and collect evidence, but it cannot generate AI scores. Select a provider and model for the Assessment Model in AI Runtime Settings.',
  '图片简历': 'Resume image',
  '姓名识别': 'Name detection',
  '导入后立即开始证据处理与 AI 评估': 'Start evidence processing and AI assessment immediately after import',
  '导入完成，尚未开始评估': 'Import complete; assessment has not started',
  '导入完成，已进入处理队列': 'Import complete and queued for processing',
  '已导入并启动处理': 'Imported and processing started',
  '已归档': 'Archived',
  '待系统识别': 'Awaiting identification',
  '文件保存在本机应用数据目录，不进入 OneDrive 项目依赖目录。': 'Files are stored in the application data directory and never placed in the OneDrive project dependency folder.',
  '文件名': 'Filename',
  '无法识别时提示人工确认': 'Request human confirmation when identification fails',
  '本地文件存储': 'Local File Storage',
  '相同姓名简历自动合并': 'Merge resumes with the same candidate name',
  '确认导入': 'Confirm Import',
  '简历': 'Resume',
  '简历文件': 'Resume file',
  '系统将依次执行文件解析、信息提取和模型评分。': 'The system will parse the files, extract information, and run model scoring in sequence.',
  '自动去除文件名中的岗位后缀': 'Remove job-title suffixes from filenames',
  '自动归档规则': 'Auto-archive Rules',
  '识别“简历 / Resume / CV”': 'Recognize “简历 / Resume / CV” filename markers',
  '请先添加简历文件': 'Add at least one resume file first',
  '请先选择归档岗位': 'Select a target job first',
  '请输入候选人姓名': 'Enter the candidate name',
  '需确认姓名': 'Name confirmation required',
  '预计候选人': 'Estimated candidates',
  '将删除候选人、材料、评估、面试与通知记录，且无法撤销。': 'This permanently deletes the candidate, files, assessments, interviews, and notification records.',
  '当前没有需要处理的候选人': 'No candidates currently require processing',
  '推荐': 'Recommended',

  // Job configuration
  'JD 已按标准结构整理': 'Job description normalized into a standard structure',
  '例如：DTC 独立站、Marketplace 或 B2B 业务': 'For example: DTC webstore, marketplace, or B2B business',
  '例如：品牌全球化与独立站转化提升期，可分行补充当前问题与优先级': 'For example: global brand expansion and webstore conversion growth; use separate lines for current issues and priorities',
  '例如：增长型 UI/UX 设计师': 'For example: Growth UI/UX Designer',
  '保存后将解析文件、统一排版并替换编辑器中的内容': 'After saving, the file will be parsed, formatted, and placed in the editor',
  '可分行说明内部与外部协作对象': 'Use separate lines for internal and external collaborators',
  '可分行说明直线和虚线汇报关系': 'Use separate lines for direct and dotted-line reporting relationships',
  '团队补位匹配': 'Team Gap Fit',
  '岗位已创建': 'Job created',
  '岗位已删除': 'Job deleted',
  '岗位相关经验': 'Job-Relevant Experience',
  '岗位配置已保存': 'Job configuration saved',
  '已恢复上次保存的岗位配置': 'Last saved job configuration restored',
  '已清空新岗位表单': 'New job form cleared',
  '当前岗位有未保存修改，确认切换吗？': 'The current job has unsaved changes. Switch anyway?',
  '当前岗位有未保存修改，确认新建吗？': 'The current job has unsaved changes. Create a new job anyway?',
  '成长潜力': 'Growth Potential',
  '新岗位': 'New Job',
  '新评分指标': 'New Scoring Criterion',
  '未命名岗位': 'Untitled Job',
  '未设置部门': 'No department',
  '杭州 / 新加坡': 'Hangzhou / Singapore',
  '核心技能匹配': 'Core Skill Fit',
  '职业路径匹配': 'Career Path Fit',
  '至少保留两个评分维度': 'Keep at least two scoring dimensions',
  '说明这个人加入后需要改变什么，而不只是完成什么任务': 'Describe what should change after this person joins, not only the tasks they should complete',
  '请先粘贴 JD 内容': 'Paste the job description first',
  '请填写岗位名称': 'Enter a job title',
  '请选择所属部门': 'Select a department',
  '（已停用）': ' (Disabled)',
  '产品负责人\n增长产品经理\n前端工程师': 'Product Lead\nGrowth Product Manager\nFrontend Engineer',
  '电商转化方法\n系统化 UX\n跨语言体验': 'E-commerce conversion methods\nSystematic UX\nCross-language experience',
  '至少 3 年相关经验\n提供可访问的作品集链接（仅人工查看）\n具备英文工作沟通能力': 'At least 3 years of relevant experience\nProvide an accessible portfolio link (human review only)\nProfessional English communication skills',

  // Model runtime and diagnostics
  'AI Run Trace、Prompt 版本、成本与质量评估': 'AI run traces, prompt versions, cost, and quality evaluation',
  'AI Run 独立快照': 'Independent AI Run Snapshots',
  'API Key 已移除': 'API key removed',
  'Custom Provider 已创建': 'Custom provider created',
  'Custom 为主要接入；DeepSeek 提供翻译与 Fallback；OpenRouter 保留为升级路径': 'Custom providers are the primary integration; DeepSeek handles translation and fallback; OpenRouter remains an upgrade path',
  'Key 已配置': 'Key configured',
  'Key 未配置': 'Key not configured',
  'Langfuse 保存失败': 'Failed to save Langfuse settings',
  'Langfuse 测试 Trace 已发送': 'Langfuse test trace sent',
  'Langfuse 连接测试失败': 'Langfuse connection test failed',
  'Langfuse 配置已保存': 'Langfuse settings saved',
  'PDF 文本层': 'PDF text layer',
  'PDF 页面渲染': 'PDF page rendering',
  'Provider 保存失败': 'Failed to save provider',
  'Provider 已保存': 'Provider saved',
  'Provider 状态': 'Provider status',
  'Provider 连接与结构化输出验证通过': 'Provider connection and structured output verified',
  'Provider 配置已保存': 'Provider settings saved',
  'Secret Key 已配置': 'Secret key configured',
  'Secret Key 未配置': 'Secret key not configured',
  '· API Key 已更新': '· API key updated',
  '上下文边界': 'Context Boundaries',
  '上游声明': 'Upstream declaration',
  '上游未声明': 'Not declared upstream',
  '不共享审核结论': 'Assessment results are not shared',
  '主 Provider 不可用时接管文本任务': 'Handles text tasks when the primary provider is unavailable',
  '仅在已确认部署区域、访问控制与数据保留策略后启用完整内容；默认 Trace 只发送来源 ID、Hash 和统计信息。': 'Enable full content only after confirming deployment region, access control, and retention policy. By default, traces send only source IDs, hashes, and statistics.',
  '传统 OCR': 'Traditional OCR',
  '保存 Context Manifest': 'Save Context Manifest',
  '保存 Langfuse': 'Save Langfuse Settings',
  '候选人证据 Token 上限': 'Candidate Evidence Token Limit',
  '候选人证据综合与岗位评分': 'Candidate evidence synthesis and job scoring',
  '减少': 'Decrease',
  '功能级 Context Pack': 'Function-Level Context Packs',
  '单个 Provider 内指数退避': 'Exponential backoff within one provider',
  '发送测试 Trace': 'Send Test Trace',
  '可用': 'Available',
  '启用模型': 'Enable model',
  '型号识别': 'Model detection',
  '增加': 'Increase',
  '处理能力': 'Processing Capabilities',
  '失败重试': 'Failure Retries',
  '完整内容': 'Full content',
  '审核上下文': 'Assessment Context',
  '已保存': 'Saved',
  '已启用': 'Enabled',
  '并发任务': 'Concurrent Tasks',
  '待保存': 'Unsaved',
  '按功能隔离': 'Isolated by function',
  '支持视觉': 'Vision supported',
  '支持视觉输入': 'Supports visual input',
  '未启用': 'Disabled',
  '本机工具可用': 'Local Tools Available',
  '本机文件与模型调用': 'Local files and model calls',
  '模型分工与处理策略已保存': 'Model roles and processing policy saved',
  '模型已删除': 'Model deleted',
  '模型已更新': 'Model updated',
  '模型已添加': 'Model added',
  '模型目录已更新': 'Model catalog updated',
  '每个独立翻译 Context': 'Each isolated translation context',
  '没有匹配的模型': 'No matching models',
  '测试 Trace 已发送': 'Test trace sent',
  '测试模型': 'Test Model',
  '独立分片翻译简历原文': 'Translate original resumes in isolated chunks',
  '移除 Key': 'Remove Key',
  '简历 OCR 模型': 'Resume OCR Model',
  '系统预设': 'System preset',
  '组织与岗位版本': 'Organization and job versions',
  '结构化结果晋升': 'Validated Structured Results',
  '结构化输出': 'Structured output',
  '响应方式': 'Response mode',
  '流式': 'Streaming',
  '非流式': 'Non-streaming',
  '缺失': 'Missing',
  '网页视觉采集': 'Web visual capture',
  '翻译分片': 'Translation Chunks',
  '能力人工配置': 'Manually configured capability',
  '脱敏': 'Redacted',
  '视觉': 'Vision',
  '记录完整模型输入输出': 'Record full model input and output',
  '证据去重': 'Evidence deduplication',
  '请选择模型': 'Select a model',
  '运行时数据': 'Runtime Data',
  '需要视觉模型': 'Vision model required',
  '非文本 PDF 与低覆盖页面视觉复读': 'Visual rereading for non-text PDFs and low-coverage pages',
  '首字节、流式空闲、总时长分层；thinking 模型最低 300 秒': 'Separate first-byte, stream-idle, and total timeouts; thinking models receive at least 300 seconds',

  // Organization, calibration, and governance
  '允许该账户进入招聘工作台': 'Allow this account to access the recruiting workspace',
  '团队账户': 'Team Accounts',
  '工作邮箱': 'Work email',
  '让模型分析、人工判断和组织权限保持清晰边界。': 'Keep model analysis, human judgment, and organizational access clearly separated.',
  '角色权限': 'Role-based access',
  '管理员、人事与招聘官使用不同操作边界': 'Administrators, HR, and hiring managers work within distinct permission boundaries',
  '安全会话': 'Secure sessions',
  '密码使用加盐密钥派生，登录会话仅存于 HttpOnly Cookie': 'Passwords use salted key derivation, and sessions are stored only in HttpOnly cookies',
  '初始化管理员登录': 'Set Up Administrator Access',
  '设置你的新密码': 'Set Your New Password',
  '登录招聘工作台': 'Sign In to Recruiting Workspace',
  '首次使用需要由现有管理员设置登录密码，完成后所有页面和 API 都会启用会话校验。': 'For first-time setup, an existing administrator must create a password. Session validation will then protect every page and API.',
  '管理员为你设置了临时密码。继续使用前，请先更换为只有你知道的新密码。': 'An administrator created a temporary password for you. Set a private password before continuing.',
  '使用工作邮箱和密码继续。账户角色决定可执行操作，招聘官只能访问已授权负责部门。': 'Continue with your work email and password. Your role controls available actions, and hiring managers can only access their assigned departments.',
  '两次输入的新密码不一致。': 'The new passwords do not match.',
  '无法继续': 'Unable to Continue',
  '当前密码': 'Current password',
  '新密码': 'New password',
  '密码': 'Password',
  '确认新密码': 'Confirm new password',
  '至少 10 个字符，同时包含字母和数字。': 'Use at least 10 characters, including letters and numbers.',
  '完成初始化并登录': 'Complete Setup and Sign In',
  '更新密码并继续': 'Update Password and Continue',
  '登录': 'Sign In',
  '登录会话 12 小时后自动失效。停用账户或管理员重置密码会立即撤销已有会话。': 'Sessions expire after 12 hours. Disabling an account or resetting its password immediately revokes existing sessions.',
  '招聘通知和 Teams 日历任务会进入可审计队列，配置组织 Graph 凭据后再由后台发送。': 'Recruiting notifications and Teams calendar tasks enter an auditable queue and are sent after organization Graph credentials are configured.',
  '新账户': 'New Account',
  '未命名账户': 'Unnamed Account',
  '未设置职务': 'No title set',
  '职务': 'Job title',
  '请填写姓名和邮箱': 'Enter a name and email',
  '账户、岗位、模型、审计和异常恢复': 'Accounts, jobs, models, audits, and recovery',
  '账户与部门权限': 'Account & Department Access',
  '账户与部门权限已保存': 'Account and department access saved',
  '账户已创建并完成部门授权': 'Account created and department access assigned',
  '账户设置': 'Account Settings',
  '例如：品牌与电商增长': 'For example: Brand and e-commerce growth',
  '修改部门名称会同步更新所属岗位': 'Renaming the department also updates its jobs',
  '允许新岗位归属到该部门': 'Allow new jobs to be assigned to this department',
  '可见范围规则': 'Visibility Rules',
  '授权账户': 'Authorized Accounts',
  '新部门': 'New Department',
  '未命名部门': 'Unnamed Department',
  '部门用于岗位归类和招聘责任分工': 'Departments organize jobs and recruiting responsibilities',
  '数据范围规则': 'Data Access Rules',
  '管理员与人事可查看全部部门；招聘官只能查看和管理已授权负责部门的岗位、候选人与简历。': 'Administrators and HR can access every department. Hiring managers can only view and manage jobs, candidates, and resumes in their assigned departments.',
  '组织结构': 'Organization Structure',
  '说明该部门负责的业务范围和常见岗位': 'Describe the department scope and common roles',
  '请填写部门名称': 'Enter a department name',
  '部门信息已保存': 'Department details saved',
  '部门名称 *': 'Department name *',
  '部门已创建': 'Department created',
  '部门已删除': 'Department deleted',
  '部门设置': 'Department Settings',
  'AI 分': 'AI Score',
  '±5 分一致率': 'Agreement Within ±5',
  '为什么不同意': 'Reason for disagreement',
  '人工分': 'Human Score',
  '人工判断待完成': 'Human decision pending',
  '从上方队列打开候选人并提交人工结论。': 'Open a candidate from the queue above and submit a human decision.',
  '低置信度': 'Low confidence',
  '分': 'points',
  '区分解析、上下文、权重和模型问题': 'Distinguish parsing, context, weighting, and model issues',
  '可计算': 'Available',
  '后续补充面试与试用期反馈': 'Add interview and probation feedback later',
  '复核': 'Review',
  '复核人': 'Reviewer',
  '已校准样本': 'Calibrated Samples',
  '平均绝对调整': 'Mean Absolute Adjustment',
  '强风险': 'High risk',
  '待样本': 'Awaiting samples',
  '排序评估': 'Ranking Evaluation',
  '最终动作': 'Final Action',
  '最终结果': 'Final Outcome',
  '记录证据与判断，不只改分': 'Record evidence and judgment, not only a score change',
  '记录重点': 'What to Record',
  '误判类型': 'Error Type',
  '达到 30 个同岗位人工样本后，再计算 NDCG、Top 10 命中率与分维度偏差。': 'Calculate NDCG, Top-10 hit rate, and dimension bias after collecting 30 human-reviewed samples for the same job.',
  '首轮校准目标': 'Initial Calibration Goal',
  '高潜力': 'High potential',
  'AI 只排序并建议人工动作': 'AI only ranks and recommends human actions',
  'AI 重评与人工校准分开存储': 'AI reassessment and human calibration are stored separately',
  '需要先配置 AI Provider': 'AI Provider Configuration Required',
  '当前没有可用于简历审核的 AI Provider 和模型。': 'No AI provider and model are currently available for resume assessment.',
  '前往模型与处理': 'Open Models & Processing',
  'AI 处理尚未启动': 'AI processing has not started',
  '无法开始处理': 'Unable to start processing',
  '请为“审核模型”选择已启用的 Provider 和 Model，并配置 API Base URL 与 API Key。': 'Select an enabled provider and model for the Assessment Model, then configure the API Base URL and API key.',
  '请联系管理员完成配置。': 'Contact an administrator to complete the configuration.',
  'AI 审核模型尚未配置或不可用。请先前往“模型与处理”，为审核模型选择已启用的 Provider 和 Model，并配置 API Base URL 与 API Key。': 'The AI assessment model is not configured or available. Open Models & Processing, select an enabled provider and model for the Assessment Model, and configure the API Base URL and API key.',
  '不自动拒绝': 'No automatic rejection',
  '受保护信号隔离': 'Protected signals isolated',
  '搜索记录': 'Search records',
  '数据保留策略': 'Data Retention Policy',
  '未解析 Provider': 'Unresolved provider',
  '未选择模型': 'No model selected',
  '查看诊断详情': 'View Diagnostics',
  '模型请求失败，未返回错误说明。': 'The model request failed without an error description.',
  '没有匹配的审计记录': 'No matching audit records',
  '生产部署时建议将审计日志写入独立 Supabase schema，并由数据库角色禁止业务账号修改历史记录。': 'For production, store audit logs in a dedicated Supabase schema and use database roles to prevent business accounts from modifying history.',
  '评分版本保留': 'Score Version Retention',
  '非候选人任务': 'Non-candidate task',

  // Talent standards
  '人才标准库视图': 'Talent Standards Library View',
  '人才标准是证据框架，不是关键词命中表': 'Talent standards are an evidence framework, not a keyword checklist',
  '仅当前岗位': 'Current Job Only',
  '候选人完成证据处理后会出现在这里。': 'Candidates appear here after evidence processing is complete.',
  '候选人观察来自当前岗位评分，不会改变组织通用库或其他岗位成功画像。': 'Candidate observations come from the current job assessment and do not alter organization-wide standards or other job profiles.',
  '反向信号': 'Counter-signals',
  '基线成熟度': 'Baseline Maturity',
  '岗位成功画像': 'Job Success Profile',
  '常见招聘岗位': 'Common Hiring Roles',
  '强证据': 'Strong Evidence',
  '当前匹配': 'Current Fit',
  '当前岗位观察': 'Current Job Observations',
  '待积累内部校准样本': 'Awaiting internal calibration samples',
  '打开岗位配置': 'Open Job Configuration',
  '标准来源': 'Standards Source',
  '核心产出': 'Core Outcomes',
  '潜力': 'Potential',
  '简历 / 作品强证据': 'Strong Resume / Work Evidence',
  '组织级标准': 'Organization Standard',
  '结构化面试问题': 'Structured Interview Questions',
  '适用于全部岗位，关注结果、学习、系统思考、商业取舍、协作和证据质量，不随当前岗位切换。': 'Applies to every job and focuses on outcomes, learning, systems thinking, business tradeoffs, collaboration, and evidence quality.',
  '通用优秀人才': 'General High-Performer Traits',
  '通用特征分类': 'General Trait Categories',
  '通用特征适用于所有岗位；岗位画像定义不同工作的成功证据；候选人仍按实际 JD、等级和团队目标独立判断。': 'General traits apply to all jobs; job profiles define role-specific success evidence; candidates are still assessed against the actual description, level, and team goals.',
  '通用简历优秀人才特征': 'General Resume-Based High-Performer Traits',
  '需要复核的风险信号': 'Risk Signals Requiring Review',
  '解析服务可用 · SQLite': 'Parsing service available · SQLite',
  '切换到中文': 'Switch to Chinese',
  '切换到英文': 'Switch to English',
  '尚未评分': 'Not scored yet',

  // Seeded talent-standard content. These are exact matches so user-created
  // standards and candidate evidence are never machine-rewritten by the UI.
  '通用能力': 'General Capability',
  '组织通用基线 v1 · 待人工校准': 'Organization Baseline v1 · Pending Human Calibration',
  'Hinomi 常见岗位基线': 'Hinomi Common Role Baseline',
  '对结果负责，而不只对任务负责': 'Own outcomes, not just tasks',
  '优秀候选人能说明目标、关键判断、个人贡献、结果口径，以及结果如何改变下一步，而不是只罗列职责。': 'Strong candidates explain the goal, key decisions, personal contribution, how results were measured, and what changed next—not just list responsibilities.',
  '有清晰基线与结果口径': 'Clear baseline and result definition',
  '区分个人贡献和团队成果': 'Separates personal contribution from team outcomes',
  '根据结果继续迭代': 'Iterates based on results',
  '只有职责清单': 'Only lists responsibilities',
  '百分比没有基线或统计周期': 'Percentages lack a baseline or measurement period',
  '高密度学习闭环': 'High-velocity learning loop',
  '面对陌生问题时能快速建立假设、主动寻求反馈、修正方法，并把一次经验沉淀为可复用能力。': 'Builds hypotheses quickly in unfamiliar situations, actively seeks feedback, adjusts the approach, and turns each experience into reusable capability.',
  '复盘具体且有方法变化': 'Retrospectives show concrete changes in approach',
  '职责增长快于年限': 'Scope grows faster than tenure',
  '主动补齐能力缺口': 'Proactively closes capability gaps',
  '工具清单增长但问题复杂度不变': 'Tool list grows while problem complexity stays flat',
  '把复杂问题沉淀为系统': 'Turn complex problems into systems',
  '不止解决单点问题，还能识别重复规则并形成流程、框架、组件或协作机制，降低后续交付成本。': 'Goes beyond one-off fixes by identifying recurring patterns and creating processes, frameworks, components, or collaboration mechanisms that reduce future delivery cost.',
  '形成可复用规则': 'Creates reusable rules',
  '能解释规则与例外': 'Explains both rules and exceptions',
  '被团队持续采用': 'Sustained adoption by the team',
  '只声称搭建体系但没有采用证据': 'Claims to have built a system without adoption evidence',
  '理解客户价值与商业取舍': 'Understand customer value and business tradeoffs',
  '能够同时考虑客户体验、收入或品牌目标、成本和执行约束，并清楚解释为什么做出当前取舍。': 'Balances customer experience, revenue or brand goals, cost, and execution constraints, and clearly explains the tradeoff made.',
  '能连接客户问题与业务指标': 'Connects customer problems to business metrics',
  '说明长期和短期权衡': 'Explains long- and short-term tradeoffs',
  '承认约束并给出替代方案': 'Acknowledges constraints and offers alternatives',
  '只谈指标或只谈主观感受': 'Discusses only metrics or only subjective opinions',
  '贡献边界与协作表达清晰': 'Clarify contribution boundaries and collaboration',
  '能说清自己的判断、团队分工、冲突点和推动过程，不把团队成果全部归于个人，也不隐藏个人贡献。': 'Clearly explains personal judgment, team responsibilities, conflicts, and how progress was driven—without claiming all team outcomes or hiding individual contribution.',
  '角色边界明确': 'Clear role boundaries',
  '能复述不同角色目标': 'Can articulate each role’s goals',
  '说明如何推动共识': 'Explains how alignment was built',
  '全程使用“我们”但无法说明个人判断': 'Uses “we” throughout but cannot explain personal judgment',
  '证据意识与复盘质量': 'Evidence discipline and retrospective quality',
  '主动保留关键证据、验证假设并复盘误差；能说明哪些结论可靠、哪些仍需验证。': 'Proactively preserves key evidence, validates hypotheses, and reviews errors; distinguishes reliable conclusions from those still requiring validation.',
  '引用原始数据或用户证据': 'References source data or user evidence',
  '主动讨论失败与偏差': 'Proactively discusses failures and bias',
  '结论包含置信边界': 'States confidence boundaries',
  '只展示成功结果': 'Shows only successful outcomes',
  '把相关性直接当成因果': 'Treats correlation as causation',

  'DTC 电商运营专员': 'DTC E-commerce Operations Specialist',
  '专员': 'Specialist',
  '稳定执行独立站商品、活动与内容运营，并能从站内数据发现问题、推动小步转化优化。': 'Reliably operates products, campaigns, and content for a DTC site, using onsite data to identify issues and drive incremental conversion improvements.',
  '商品、价格、库存与页面信息准确上线': 'Launch accurate product, pricing, inventory, and page information',
  '活动按期交付并复盘流量、转化率和客单价': 'Deliver campaigns on time and review traffic, conversion rate, and average order value',
  '识别站内漏斗问题并推动可验证优化': 'Identify onsite funnel issues and drive verifiable improvements',
  '与广告、内容、客服和供应链保持执行闭环': 'Maintain an execution loop across paid media, content, service, and supply chain',
  '明确说明独立负责的站点、市场和业务规模': 'Clearly states independently owned sites, markets, and business scale',
  '给出转化率、客单价、复购或活动结果的基线与周期': 'Provides baselines and periods for conversion, order value, repeat purchase, or campaign results',
  '展示一次从数据发现问题到落地验证的完整过程': 'Shows an end-to-end example from data diagnosis to implementation and validation',
  '工具使用与具体业务动作绑定，而非只列 Shopify、GA4 等名称': 'Connects tool use to business actions instead of merely listing Shopify, GA4, and similar tools',
  '工作内容停留在后台维护和机械上架': 'Work is limited to backend maintenance and routine listings',
  '只报告 GMV，没有流量、折扣或个人贡献口径': 'Reports GMV without traffic, discount, or personal-contribution context',
  '所有增长动作依赖打折，缺少站内体验与复购意识': 'Relies on discounts for all growth, with little attention to onsite experience or retention',
  '举例说明一次转化率下降，你如何定位问题并验证？': 'Give an example of a conversion-rate decline. How did you diagnose and validate it?',
  '最近一次活动中你独立负责什么，结果如何归因？': 'What did you independently own in your most recent campaign, and how did you attribute the result?',
  '商品、库存、广告和站内内容冲突时如何排优先级？': 'How do you prioritize conflicts among products, inventory, advertising, and onsite content?',
  '站点与商品运营': 'Site & Merchandising Operations',
  '数据诊断': 'Data Diagnosis',
  '转化率优化': 'Conversion Optimization',
  '活动执行': 'Campaign Execution',
  '复购与生命周期': 'Retention & Lifecycle',
  '跨团队协同': 'Cross-functional Collaboration',

  'DTC 电商运营主管': 'DTC E-commerce Operations Lead',
  '主管': 'Lead',
  '对独立站收入质量和运营节奏负责，建立预测、复盘和团队机制，而不是只做高级执行。': 'Owns DTC revenue quality and operating cadence, building forecasting, review, and team mechanisms rather than acting only as a senior executor.',
  '对 GMV、毛利、转化、客单价和复购形成完整经营视图': 'Build a complete operating view across GMV, margin, conversion, order value, and retention',
  '制定月度与季度运营节奏并动态调整资源': 'Set monthly and quarterly operating rhythms and dynamically adjust resources',
  '联动广告、品牌、产品、供应链和客服解决经营问题': 'Coordinate paid media, brand, product, supply chain, and service to solve operating issues',
  '建立团队 SOP、目标拆解和人才培养机制': 'Build team SOPs, goal breakdowns, and talent-development mechanisms',
  '同时提供收入、毛利、CAC 或库存等约束指标': 'Provides revenue alongside constraints such as margin, CAC, or inventory',
  '说明预算和资源如何在不同市场、渠道或活动间分配': 'Explains how budget and resources were allocated across markets, channels, or campaigns',
  '有预测偏差、失败活动和调整动作的真实复盘': 'Provides honest reviews of forecast misses, failed campaigns, and corrective actions',
  '明确团队规模、直接管理范围和机制建设结果': 'Clarifies team size, direct management scope, and results of mechanism building',
  '只强调销售额，不讨论利润和流量成本': 'Emphasizes sales without discussing profit or traffic cost',
  '主要依赖代理商，无法解释关键经营判断': 'Relies mainly on agencies and cannot explain key operating decisions',
  '职位是主管但没有目标拆解、辅导或机制建设证据': 'Holds a lead title without evidence of goal setting, coaching, or mechanism building',
  '当 GMV 增长但利润恶化时，你如何拆解并调整？': 'When GMV grows but profit deteriorates, how do you diagnose and adjust?',
  '描述一次预测失准，以及你如何修正运营节奏。': 'Describe a forecast miss and how you corrected the operating cadence.',
  '你建立过什么机制，让团队不依赖个人救火？': 'What mechanism have you built so the team does not depend on individual firefighting?',
  '经营结果质量': 'Quality of Business Outcomes',
  '策略与节奏': 'Strategy & Cadence',
  '数据与预测': 'Data & Forecasting',
  '团队管理': 'Team Management',
  '转化与复购': 'Conversion & Retention',
  '跨市场协同': 'Cross-market Collaboration',

  'DTC 品牌营销总监': 'DTC Brand Marketing Director',
  '总监': 'Director',
  '把品牌定位、消费者洞察和整合营销转化为可持续需求与商业增长，并建立跨市场团队能力。': 'Turns brand positioning, consumer insight, and integrated marketing into sustainable demand and commercial growth while building cross-market team capability.',
  '建立清晰定位、受众和品牌叙事体系': 'Establish clear positioning, audiences, and brand narrative',
  '统筹品牌、内容、PR、社媒、投放和电商形成整合战役': 'Integrate brand, content, PR, social, paid media, and e-commerce into coordinated campaigns',
  '同时管理品牌健康、需求增长与收入质量': 'Manage brand health, demand growth, and revenue quality together',
  '配置预算、团队和代理商，并推动全球一致与本地适配': 'Allocate budgets, teams, and agencies while balancing global consistency with local adaptation',
  '品牌策略带来认知、搜索、自然流量或转化变化': 'Brand strategy drives changes in awareness, search, organic traffic, or conversion',
  '有跨渠道整合战役及各渠道角色分工': 'Shows integrated cross-channel campaigns with clear channel roles',
  '说明预算规模、团队结构和资源取舍': 'Explains budget scale, team structure, and resource tradeoffs',
  '能区分短期销售贡献与长期品牌资产': 'Distinguishes short-term sales contribution from long-term brand equity',
  '只有曝光、获奖或声量，没有商业连接': 'Shows reach, awards, or buzz without a commercial connection',
  '作品主要由代理商完成，个人判断与推动边界不清': 'Work was mainly delivered by agencies, with unclear personal judgment and ownership',
  '品牌主张宏大，但没有消费者证据和落地一致性': 'Brand claims are ambitious but lack consumer evidence and execution consistency',
  '举例说明一个品牌策略如何改变了业务决策和消费者行为。': 'Give an example of how a brand strategy changed business decisions and consumer behavior.',
  '预算被削减 30% 时，你保留什么、放弃什么？': 'If the budget is cut by 30%, what do you keep and what do you stop?',
  '如何证明一次整合营销不是渠道结果的简单相加？': 'How do you prove an integrated campaign is more than the sum of channel results?',
  '品牌战略与定位': 'Brand Strategy & Positioning',
  '整合营销': 'Integrated Marketing',
  '商业增长': 'Commercial Growth',
  '消费者洞察': 'Consumer Insight',
  '组织与预算': 'Organization & Budget',
  '全球本地化': 'Global Localization',

  '品牌社媒 PR 营销专家': 'Brand Social & PR Marketing Specialist',
  '专家': 'Expert',
  '建立有辨识度的品牌内容与公共关系网络，让自然声量、创作者合作和媒体影响能够被持续运营与衡量。': 'Builds distinctive brand content and public-relations networks so organic attention, creator partnerships, and media influence can be operated and measured over time.',
  '形成稳定的社媒内容机制与品牌语气': 'Build a consistent social-content system and brand voice',
  '建立创作者、媒体和行业关系并获得高质量自然传播': 'Build creator, media, and industry relationships that earn high-quality organic reach',
  '把社媒与 PR 影响连接到搜索、站点流量或品牌认知': 'Connect social and PR impact to search, site traffic, or brand awareness',
  '具备舆情判断、危机响应和跨市场沟通能力': 'Demonstrate reputation judgment, crisis response, and cross-market communication',
  '展示内容策略、发布机制和连续结果，而非单条爆款': 'Shows content strategy, publishing mechanisms, and sustained results—not a single viral post',
  '区分自然传播、付费合作和媒体转载': 'Distinguishes organic reach, paid partnerships, and media syndication',
  '有创作者筛选、brief、权益和效果复盘过程': 'Shows creator selection, briefing, rights, and performance review processes',
  '说明舆情或危机中的判断链和协同机制': 'Explains the decision chain and coordination mechanism during reputation issues or crises',
  '只用粉丝数、曝光量证明能力': 'Uses follower count and impressions as the only evidence',
  '把付费投放结果包装成自然增长': 'Presents paid-media results as organic growth',
  '内容审美不错但缺少品牌一致性和受众洞察': 'Content looks good but lacks brand consistency and audience insight',
  '如何判断一次 PR 或社媒传播真正影响了品牌？': 'How do you determine whether PR or social activity genuinely influenced the brand?',
  '描述一次创作者合作效果不佳，你如何定位原因？': 'Describe an underperforming creator partnership. How did you diagnose the cause?',
  '遇到负面舆情的前两小时你会做什么？': 'What would you do in the first two hours of a negative reputation event?',
  '内容与品牌语气': 'Content & Brand Voice',
  '创作者与媒体关系': 'Creator & Media Relations',
  '自然增长': 'Organic Growth',
  '测量与归因': 'Measurement & Attribution',
  '跨市场传播': 'Cross-market Communications',
  '舆情与危机': 'Reputation & Crisis',

  'DTC 广告投放专家': 'DTC Paid Media Specialist',
  '在可控的 CAC、MER 和利润约束下扩量，能同时负责媒体策略、追踪归因、创意实验和落地页协同。': 'Scales within controlled CAC, MER, and profit constraints while owning media strategy, tracking and attribution, creative experimentation, and landing-page collaboration.',
  '制定 Meta、Google、TikTok 等渠道组合与预算节奏': 'Set the channel mix and budget cadence across Meta, Google, TikTok, and similar platforms',
  '在规模、CAC、MER、LTV 和毛利之间做经营取舍': 'Make operating tradeoffs across scale, CAC, MER, LTV, and gross margin',
  '建立创意测试、受众测试和学习沉淀机制': 'Build mechanisms for creative testing, audience testing, and accumulated learning',
  '保证像素、事件、归因和报表口径可信': 'Ensure pixels, events, attribution, and reporting definitions are trustworthy',
  '说明真实花费规模、市场、周期和业务阶段': 'States actual spend scale, market, period, and business stage',
  '提供扩量前后 CAC、MER、边际回报和利润约束': 'Provides CAC, MER, marginal return, and profit constraints before and after scaling',
  '展示假设、样本、变量和结论清晰的创意实验': 'Shows creative experiments with clear hypotheses, samples, variables, and conclusions',
  '能解释平台归因、GA4 和增量判断之间的差异': 'Explains differences among platform attribution, GA4, and incrementality judgments',
  '只展示平台 ROAS，不讨论归因和利润': 'Shows only platform ROAS without discussing attribution or profit',
  '挑选单个高光周期，隐藏扩量后的效率衰减': 'Cherry-picks one strong period and hides efficiency decay after scaling',
  '把创意、落地页或产品问题全部归因于媒体': 'Attributes creative, landing-page, or product issues entirely to media',
  'ROAS 很高但 MER 没有变化，你会如何判断？': 'ROAS is high but MER is unchanged. How would you assess the situation?',
  '描述一次从小预算到扩量的边际变化和决策。': 'Describe the marginal changes and decisions involved in scaling from a small budget.',
  '如何设计实验区分创意、受众和落地页影响？': 'How would you design an experiment to separate creative, audience, and landing-page effects?',
  '媒体策略': 'Media Strategy',
  '创意实验': 'Creative Experimentation',
  '单位经济模型': 'Unit Economics',
  '规模化能力': 'Scaling Capability',
  '落地页协同': 'Landing-page Collaboration',

  'UI/UX 设计师': 'UI/UX Designer',
  '设计师': 'Designer',
  '用简历中可验证的职责、项目复杂度、协作过程和商业结果判断岗位匹配；作品集仅供设计负责人手动查看。': 'Assesses role fit using verifiable resume evidence about ownership, project complexity, collaboration, and business outcomes; portfolios remain for manual design-lead review only.',
  '把用户问题和业务目标转化为清晰体验路径': 'Translate user problems and business goals into clear experience journeys',
  '交付稳定的多端视觉、组件和状态设计': 'Deliver consistent multi-platform visual, component, and state design',
  '与产品、研发、增长共同推动上线和验证': 'Partner with product, engineering, and growth to launch and validate',
  '在 DTC 场景中持续改善发现、决策、购买与售后体验': 'Continuously improve discovery, decision, purchase, and post-purchase experiences in DTC contexts',
  '简历明确说明项目问题、职责边界、协作对象和上线结果': 'Resume clearly states the problem, ownership boundaries, collaborators, and launch results',
  '关键流程写明用户研究、数据或可用性验证': 'Key flows document user research, data, or usability validation',
  '有组件规范、多端交付或设计系统协作经历': 'Shows experience with component standards, multi-platform delivery, or design-system collaboration',
  '真实商业项目包含可核验的迭代和结果口径': 'Real commercial projects include verifiable iterations and result definitions',
  '只列交付物和工具，没有问题定义与个人判断': 'Lists deliverables and tools without problem definition or personal judgment',
  '项目描述缺少职责边界、落地状态或结果口径': 'Project descriptions lack ownership boundaries, launch status, or result definitions',
  '声称负责完整体验但没有研究、协作或验证证据': 'Claims end-to-end experience ownership without research, collaboration, or validation evidence',
  '选一个项目说明最关键的设计判断和被放弃的方案。': 'Choose one project and explain the most important design decision and the alternative you rejected.',
  '用户目标和转化目标冲突时你如何处理？': 'How do you handle conflict between user goals and conversion goals?',
  '上线后哪项证据让你改变了原设计？': 'What post-launch evidence caused you to change the original design?',
  '岗位相关项目经验': 'Role-Relevant Project Experience',
  'UX 与用户路径': 'UX & User Journeys',
  'DTC 转化体验': 'DTC Conversion Experience',
  '设计系统协作': 'Design-System Collaboration',
  '研究与验证': 'Research & Validation',
  '落地协作': 'Delivery Collaboration',

  '人体工学产品工业设计师': 'Ergonomic Product Industrial Designer',
  '工业设计师': 'Industrial Designer',
  '把人体工学研究、产品创新和造型语言转化为可量产、可验证、成本与可靠性受控的实体产品。': 'Turns ergonomics research, product innovation, and form language into manufacturable, testable physical products with controlled cost and reliability.',
  '把人体尺寸、姿态、压力与使用场景转化为设计要求': 'Translate body dimensions, posture, pressure, and use contexts into design requirements',
  '形成有品牌辨识度且满足结构约束的产品方案': 'Create structurally feasible product concepts with distinctive brand character',
  '通过样机、用户测试和工程验证持续迭代': 'Iterate through prototypes, user tests, and engineering validation',
  '与结构、供应链、模具和工厂推动量产质量': 'Drive production quality with engineering, supply chain, tooling, and factories',
  '至少一个已量产产品的完整职责链': 'End-to-end ownership evidence for at least one mass-produced product',
  '有人体工学假设、样本、测试方法和设计变化': 'Shows ergonomic hypotheses, samples, test methods, and resulting design changes',
  '说明 CMF、结构、材料、成本和制造取舍': 'Explains CMF, structure, material, cost, and manufacturing tradeoffs',
  '展示样机迭代、失效问题和供应商协同': 'Shows prototype iterations, failure issues, and supplier collaboration',
  '简历只写造型或渲染职责，没有样机和量产经历': 'Resume mentions only styling or rendering, with no prototype or production experience',
  '只说“更舒适”，没有人体工学验证方法': 'Claims “more comfortable” without an ergonomic validation method',
  '造型贡献清晰但结构、成本和制造边界模糊': 'Styling contribution is clear, but structural, cost, and manufacturing boundaries are vague',
  '一个人体工学结论如何转化为具体尺寸或机构？': 'How did an ergonomic conclusion translate into a specific dimension or mechanism?',
  '描述一次样机测试推翻原方案的经历。': 'Describe a prototype test that overturned the original concept.',
  '造型、成本、可靠性冲突时你如何与工程团队决策？': 'How do you decide with engineering when form, cost, and reliability conflict?',
  '人体工学研究': 'Ergonomics Research',
  '产品创新': 'Product Innovation',
  '工程与 DFM': 'Engineering & DFM',
  '造型与 CMF': 'Form & CMF',
  '样机与验证': 'Prototyping & Validation',
  '供应链协同': 'Supply-chain Collaboration',

  // Candidate profile and review workflow
  '线下为主': 'Offline-led',
  '全渠道': 'Omnichannel',
  '分销/经销': 'Distribution / Reseller',
  '制造': 'Manufacturing',
  '业务类型未知': 'Business type unknown',
  '微型': 'Micro',
  '小型': 'Small',
  '中型': 'Medium',
  '大型': 'Large',
  '集团/企业级': 'Enterprise',
  '规模未知': 'Scale unknown',
  '简历明示': 'Explicitly stated in resume',
  '业务描述推断': 'Inferred from business description',
  '广泛已知': 'Widely known',
  '待核实': 'To verify',
  '风险尚未评估': 'Risks not assessed yet',
  '文件解析与评分完成后，这里会显示需要人工确认的问题': 'Questions requiring human confirmation will appear after parsing and scoring are complete',
  '未发现高优先级风险': 'No high-priority risks found',
  '仍建议在面试中验证关键证据': 'Key evidence should still be verified in the interview',
  '待评估': 'Pending assessment',
  '当前证据不足，等待人工观察。': 'Current evidence is insufficient; awaiting human observation.',
  '时间未识别': 'Date not identified',
  '院校未识别': 'School not identified',
  '院校层级': 'Institution Tier',
  '岗位相关专业': 'Job-Relevant Major',
  '学历 / 专业未识别': 'Degree / major not identified',
  '重新评估后从简历原文抽取': 'Extract from the original resume after reassessment',
  '公司未识别': 'Company not identified',
  '职位未识别': 'Role not identified',
  '仅展示有广泛知名度依据的任职公司；普通公司和客户品牌不会列入': 'Only employers with clear, broad notability are shown; ordinary companies and client brands are omitted',
  '语言未识别': 'Language not identified',
  '未明确写出的熟练度不会推断': 'Proficiency is not inferred unless explicitly stated',
  '电话': 'Phone',
  '仅展示简历中明确写出的邮箱和电话': 'Only email and phone details explicitly stated in the resume are shown',
  '只展示简历明确出现的学习、工作或市场地区，不推断国籍': 'Only education, work, or market regions explicitly stated in the resume are shown; nationality is never inferred',
  '不用于推断年龄': 'Never used to infer age',
  '缺少可确认的工作起始年份': 'No verifiable career start year',
  '专业经历': 'Professional Experience',
  '工作经验': 'Work Experience',
  '工作经历': 'Employment History',
  '职业经历': 'Career History',
  '教育经历': 'Education',
  '项目经验': 'Project Experience',
  '项目经历': 'Projects',
  '证书': 'Certificates',
  '认证': 'Certifications',
  '荣誉': 'Honors',
  '个人信息': 'Personal Information',
  '个人简介': 'Profile',
  '个人概况': 'Profile',
  '专业概要': 'Professional Summary',
  '职业概述': 'Career Summary',
  '求职目标': 'Career Objective',
  '核心能力': 'Core Capabilities',
  '核心技能': 'Core Skills',
  '专业技能': 'Professional Skills',
  '技术技能': 'Technical Skills',
  '简历重点标注': 'Resume Highlights',
  '重点标注': 'Highlights',
  '尚无可查看的简历文本': 'No resume text available',
  '请先处理简历文件；解析完成后原文会显示在这里。': 'Process the resume first; the original text will appear here after parsing.',
  '简历文本版本': 'Resume text version',
  '按内容结构整理的简历原文': 'Original resume organized by content structure',
  '解析后的简历原文': 'Parsed original resume',
  '简历资产': 'Resume assets',
  '该翻译版本没有返回文本。': 'This translation version returned no text.',
  '点击页面顶部“生成翻译”后，译文会在这里与原文并列保存。': 'After selecting “Generate Translation” at the top, the translation will be saved here alongside the original.',
  '作品集链接': 'Portfolio link',
  '作品集文件': 'Portfolio file',
  '打开候选人提交的原文件': 'Open the original file submitted by the candidate',
  '人工查看': 'Human Review',
  '未提供可访问的作品集': 'No accessible portfolio provided',
  '候选人提交链接或原文件后会显示在这里': 'Links or original files submitted by the candidate will appear here',
  '系统不会打开、截图、总结或评价这些材料，也不会让作品集影响候选人分数。': 'The system does not open, capture, summarize, or evaluate these materials, and portfolios never affect candidate scores.',
  '发展轨迹': 'Career Progression',
  '岗位等级匹配': 'Level Fit',
  '稳定性': 'Stability',
  '业务环境匹配': 'Business Environment Fit',
  '业务规模匹配': 'Business Scale Fit',
  '历史评估未包含': 'Not included in the previous assessment',
  '公司 / 职位': 'Company / Role',
  '行业与业务类型': 'Industry & Business Type',
  '规模级别': 'Scale Level',
  '信息依据': 'Evidence Basis',
  '目标岗位关联': 'Relevance to Target Job',
  '公司未知': 'Company unknown',
  '职位未注明': 'Role not specified',
  '行业未知': 'Industry unknown',
  '缺少公司业务描述。': 'Company business description is missing.',
  '缺少规模证据。': 'Scale evidence is missing.',
  '尚未判断与目标岗位的关联。': 'Relevance to the target job has not been assessed.',
  '历史评估未包含逐家公司背景': 'Previous assessment did not include company-by-company background',
  'AI 重审后，将展示行业、B2B/B2C/DTC/线下等业务类型及规模依据。': 'After AI reassessment, this section will show industry, B2B/B2C/DTC/offline business types, and scale evidence.',
  '业务环境证据': 'Business Environment Evidence',
  '业务规模证据': 'Business Scale Evidence',
  '当前评估没有足够证据。': 'The current assessment does not contain enough evidence.',
  '等待更多结构化履历证据': 'Awaiting more structured career evidence',
  '履历与项目证据': 'Career and Project Evidence',
  '职责范围变化': 'Changes in Scope',
  '所有受保护属性': 'all protected attributes',
  '候选人审核': 'Candidate Assessment',
  '历史作品集评审（已停用）': 'Historical Portfolio Review (Disabled)',
  '简历 OCR': 'Resume OCR',
  '简历翻译': 'Resume Translation',
  '首次复核后会在这里形成完整时间线。': 'A complete timeline will appear after the first review.',
  '未填写流转备注': 'No workflow note',
  '分钟': 'minutes',
  '未填写备注': 'No notes',
  '首次复核后，人工分数、结论和备注会记录在这里。': 'Human scores, decisions, and notes will appear after the first review.',
  '候选人执行模型任务后会在这里生成独立上下文记录。': 'An isolated context record will appear here after the candidate runs a model task.',
  '来源': 'Sources',
  '估算 tokens': 'Estimated tokens',
  '面试': 'Interview',
  '入职': 'Hired',
  '确认已发 Offer': 'Confirm Offer Sent',
  '记录接受 Offer': 'Record Offer Acceptance',
  '记录拒绝 Offer': 'Record Offer Decline',
  '确认已入职': 'Confirm Hired',
  '登记面试时间': 'Schedule Interview Time',
  '请选择面试时间和至少一位面试官': 'Select an interview time and at least one interviewer',
  '面试已登记，邮件与 Teams 日历任务已创建': 'Interview scheduled; email and Teams calendar tasks created',
  '候选人时间确认后，再创建面试官通知与 Teams 日历任务。': 'Create interviewer notifications and Teams calendar tasks after the candidate confirms the time.',
  '时长': 'Duration',
  '形式': 'Format',
  '其他视频会议': 'Other video meeting',
  '办公室或会议链接': 'Office or meeting link',
  '面试保存后生成邮件和 Teams Calendar 任务；在 Microsoft Graph 配置完成前保持待发送状态。': 'Saving the interview creates email and Teams Calendar tasks, which remain pending until Microsoft Graph is configured.',
  '当前复核人': 'Current reviewer',
  '人工校准已保存并进入审计记录': 'Human calibration saved to the audit log',
  '校准只影响排序分；招聘阶段请在候选人流程中单独更新。': 'Calibration affects only ranking; update the recruiting stage separately in the candidate workflow.',
  '人工调整分': 'Human-Adjusted Score',
  '记录关键证据、不同意 AI 的原因或后续面试问题': 'Record key evidence, reasons for disagreeing with AI, or follow-up interview questions',
  '该候选人的旧版摘要包含已停用的作品集评估，当前不再展示。下方分数已排除作品集维度；AI 重审后会生成仅基于简历证据的新版摘要。': 'This candidate’s legacy summary included a now-disabled portfolio assessment and is no longer shown. The scores below exclude the portfolio dimension; AI reassessment will generate a new resume-only summary.',
  '材料已归档，尚未开始处理。点击“开始评估”后，系统会先解析简历，再调用已配置的模型生成评估。': 'Files are archived but processing has not started. Select “Start Assessment” to parse the resume and run the configured assessment model.',
  '系统正在处理或等待评分结果。': 'The system is processing or waiting for scoring results.',
  '候选人已进入系统处理队列': 'Candidate added to the processing queue',
  '，已生成通知人事任务': '; an HR notification task was created',
  '演示': 'Demo',
  '请先处理并解析简历文本': 'Process and parse the resume text first',
  '开始评估': 'Start Assessment',
  '开始约面': 'Start Scheduling',
  '教育': 'Education',
  '知名公司': 'Notable Company',
  '该专业背景与目标岗位相关': 'This academic background is relevant to the target job',
  '英语': 'English',
  '普通话': 'Mandarin',
  '泰米尔语': 'Tamil',
  '印地语': 'Hindi',
  '越南语': 'Vietnamese',
  '马来语': 'Malay',
  '印尼语': 'Indonesian',
  '泰语': 'Thai',
  '菲律宾语': 'Filipino',
  '他加禄语': 'Tagalog',
  '日语': 'Japanese',
  '韩语': 'Korean',
  '法语': 'French',
  '德语': 'German',
  '西班牙语': 'Spanish',
  '阿拉伯语': 'Arabic',
  '粤语': 'Cantonese',
  '新加坡': 'Singapore',
  '越南': 'Vietnam',
  '印度（班加罗尔）': 'India (Bangalore)',
  '印度（哥印拜陀）': 'India (Coimbatore)',
  '印度': 'India',
  '阿联酋（迪拜）': 'UAE (Dubai)',
  '阿联酋': 'United Arab Emirates',
  '马来西亚': 'Malaysia',
  '菲律宾': 'Philippines',
  '印度尼西亚': 'Indonesia',
  '泰国': 'Thailand',
  '中国': 'China',
  '中国香港': 'Hong Kong, China',
  '美国': 'United States',
  '英国': 'United Kingdom',
  '澳大利亚': 'Australia',
  '全球四大会计师事务所之一': 'One of the global Big Four accounting firms',
  '全球头部科技公司': 'Leading global technology company',
  '全球头部电商与科技公司': 'Leading global e-commerce and technology company',
  '全球头部消费电子与科技公司': 'Leading global consumer electronics and technology company',
  '全球头部互联网平台': 'Leading global internet platform',
  '全球头部社交内容平台': 'Leading global social content platform',
  '中国头部互联网平台': 'Leading Chinese internet platform',
  '中国头部电商与科技公司': 'Leading Chinese e-commerce and technology company',
  '全球头部电商平台': 'Leading global e-commerce platform',
  '东南亚头部电商平台': 'Leading Southeast Asian e-commerce platform',
  '全球头部运动品牌': 'Leading global sportswear brand',
  '全球头部消费品公司': 'Leading global consumer goods company',
  '全球知名消费科技公司': 'Globally recognized consumer technology company',
  '全球头部消费电子公司': 'Leading global consumer electronics company',
  '全球头部通信与消费电子公司': 'Leading global telecom and consumer electronics company',
  '交互设计': 'Interaction Design',
  '产品设计': 'Product Design',
  '人机交互': 'Human-Computer Interaction',
  '人类交互设计': 'Human Interaction Design',
  '工业设计': 'Industrial Design',
  '市场营销': 'Marketing',
  '电子商务': 'E-commerce',
  '视觉传达': 'Visual Communication',
  '三星': 'Samsung',
  '亚马逊': 'Amazon',
  '华为': 'Huawei',
  '字节跳动': 'ByteDance',
  '安永': 'EY',
  '微软': 'Microsoft',
  '德勤': 'Deloitte',
  '戴森': 'Dyson',
  '抖音国际版': 'TikTok',
  '普华永道': 'PwC',
  '来赞达': 'Lazada',
  '毕马威': 'KPMG',
  '耐克': 'Nike',
  '联合利华': 'Unilever',
  '腾讯': 'Tencent',
  '苹果公司': 'Apple',
  '虾皮': 'Shopee',
  '谷歌': 'Google',
  '阿迪达斯': 'Adidas',
  '阿里巴巴': 'Alibaba',

  // Server task and API messages
  '正在准备证据处理': 'Preparing evidence processing',
  '演示候选人已包含完整评估结果': 'Demo candidate already contains a complete assessment',
  '缺少可处理文件，已转入人工复核': 'No processable files; moved to human review',
  '未获得可用证据，已转入人工复核': 'No usable evidence found; moved to human review',
  '证据采集完成；等待模型配置': 'Evidence collection complete; waiting for model configuration',
  '按岗位权重生成可追溯评分卡': 'Generating a traceable scorecard from job weights',
  '公司背景搜索': 'Company background research',
  '公司背景搜索未完成，将以简历证据继续审核': 'Company research did not finish; continuing with resume evidence',
  '未识别到可搜索的履历公司': 'No searchable employers were identified',
  '评分完成，已进入人工复核': 'Scoring complete; moved to human review',
  '简历评分已完成': 'Resume scoring complete',
  '评分与证据链已完成': 'Scoring and evidence chain complete',
  'Provider 不存在': 'Provider not found',
  '任务不存在': 'Task not found',
  '候选人不存在': 'Candidate not found',
  '你没有权限查看该部门的简历。': 'You do not have permission to view resumes from this department.',
  '无法查看候选人': 'Unable to view candidate',
  '返回候选人列表': 'Back to candidate list',
  '候选人正在处理，请等待任务结束后再删除': 'The candidate is being processed. Wait for the task to finish before deleting.',
  '内置 Provider 不能删除，可以停用。': 'Built-in providers cannot be deleted; disable them instead.',
  '历史导入已修复，等待简历处理': 'Historical import repaired; waiting for resume processing',
  '原文件不可用': 'Original file unavailable',
  '岗位不存在': 'Job not found',
  '已进入系统处理队列': 'Queued for processing',
  '简历翻译': 'Resume translation',
  '综合性大学': 'Comprehensive University',
  '理工类院校': 'Technical Institution',
  '师范类院校': 'Normal University',
  '艺术类院校': 'Arts Academy',
  '商科院校': 'Business School',
  '医药类院校': 'Medical University',
  '农林类院校': 'Agricultural University',
  '外国语类院校': 'Foreign Studies University',
  '政法类院校': 'Law University',
  '体育类院校': 'Sports University',
  '理工学院': 'Polytechnic',
  '职业技术院校': 'Vocational Institute',
  '院校类型未知': 'Institution type unknown',
  '双一流': 'Double First Class',
  '该候选人已有任务在进行，请等待当前任务结束后再试。': 'This candidate already has a task running. Wait for it to finish and try again.',
  '翻译进行中': 'Translating',
  '简历翻译已进入任务队列': 'Resume translation queued',
  '简历译文已生成': 'Resume translation ready',
  '简历翻译失败': 'Resume translation failed',
  '已排队：正在等待翻译模型': 'Queued: waiting for the translation model',
  '正在准备简历翻译': 'Preparing the resume translation',
  '正在翻译简历': 'Translating the resume',
  '正在保存译文': 'Saving the translation',
  '翻译已由用户取消': 'Translation cancelled by the user',
  '排队简历翻译': 'Queue resume translation',
  '生成简历译文': 'Generate resume translation',
  '候选人处理失败': 'Candidate processing failed',
  '没有可翻译的简历文本': 'There is no resume text to translate',
  '翻译模型尚未配置或不可用。请先前往“模型与处理”，为翻译模型选择已启用的 Provider 和 Model。': 'The translation model is not configured or unavailable. Choose an enabled provider and model for it under Models & Processing.',
  '已排队：将先解析文件，再生成评估': 'Queued: files will be parsed before assessment',
  '模型不存在': 'Model not found',
  '没有可翻译的简历文本': 'No translatable resume text',
  '等待重试': 'Waiting to retry',
  '该任务已经排队或正在处理': 'This task is already queued or processing',
  '该候选人已有任务排队或正在处理': 'This candidate already has a queued or active task',
  '请选择人工结论': 'Select a human decision',
  '请选择候选人或输入姓名': 'Select a candidate or enter a name',
  '请选择有效岗位': 'Select a valid job',
  '账户不存在': 'Account not found',
  '资产不存在': 'Asset not found',
  '这是历史失败记录，请重试该候选人的最新任务': 'This is a historical failure. Retry the candidate’s latest task.',
  '部门不存在': 'Department not found',
  '无法连接本地服务，请确认 http://127.0.0.1:4174 正在运行后重试。': 'Cannot connect to the local service. Confirm that http://127.0.0.1:4174 is running and try again.',
  '系统错误': 'System error',
  '搜索服务': 'Search Service',
  '公司背景核验的独立检索来源；不配置则回退到模型自带的联网搜索': 'Independent retrieval source for company verification; falls back to the model built-in web search when unset',
  '服务商': 'Provider',
  '检索模式': 'Retrieval Mode',
  '搜索服务 API Key': 'Search service API key',
  '结果条数': 'Results per Company',
  '每家公司检索返回上限': 'Maximum results returned per company',
  '单条摘录': 'Excerpt Length',
  '交给模型阅读的字符上限': 'Character limit passed to the model',
  '保存搜索服务': 'Save Search Service',
  '检查凭据': 'Check Credential',
  '尚未检查凭据': 'Credential not checked yet',
  '搜索服务配置已保存': 'Search service settings saved',
  '搜索服务保存失败': 'Could not save search service settings',
  '搜索服务可用': 'Search service is working',
  '搜索服务不可用': 'Search service is unavailable',
  '搜索服务检查失败': 'Search service check failed',
  '公司背景核验将改用该服务检索。': 'Company verification will now retrieve through this service.',
  '仍需启用并填写 API Key 后才会生效。': 'It takes effect only once the service is enabled and an API key is saved.',
  'API Key 已保存，但开关还没打开': 'API key saved, but the switch is still off',
  '搜索服务当前未启用，公司背景核验仍走模型自带的联网搜索。把右上角开关打开并保存后才会改用该服务。': 'The search service is disabled, so company verification still uses the model built-in web search. Turn on the switch above and save to route retrieval through this service.',
  '当前使用模型自带联网搜索': 'Currently using the model built-in web search',
  '填写 API Key 并启用后，公司背景核验会先由搜索服务取回真实网页摘录，再交给模型归纳，引用只能来自这批结果。': 'Once an API key is saved and the service is enabled, company verification retrieves real page excerpts first and the model only summarises them, so citations cannot go beyond those results.',
  '公司背景核验已改用搜索服务': 'Company verification now uses the search service',
  '模型只阅读检索到的网页摘录并归纳，引用无法超出这批结果。「公司背景核验模型」角色仍在使用，但它现在负责归纳，不需要自带联网能力。': 'The model only reads and summarises the retrieved page excerpts, so citations cannot go beyond them. The Company Research Model role is still used, but it now summarises and no longer needs its own web search.',
  '联网搜索协议': 'Web Search Protocol',
  '不启用': 'Disabled',
  'GLM 搜索': 'GLM Search',
  'xAI 搜索': 'xAI Search',
  '需要搜索协议': 'Web search protocol required',
  '检索由搜索服务提供': 'Retrieval via search service',
  '公司背景核验模型': 'Company Research Model',
  '公司搜索模型': 'Company Research Model',
  '核验履历公司背景并保存来源；配置搜索服务后只负责归纳检索结果': 'Verifies employers from the resume and records sources; only summarises retrieved results once a search service is configured',
  '条': 'items',
  '字符': 'chars',
  '个': 'tasks',
  '次': 'retries',
  '秒': 's',
  '服务重启后恢复等待': 'Waiting again after the service restarted',
  '服务重启前任务未正常结束': 'The task did not finish before the service restarted',
  '服务重启前请求未正常结束': 'The request did not finish before the service restarted',
  '正在生成中英文诊断内容': 'Generating the bilingual assessment',
  '联网核验': 'Web verified',
  '联网有限推断': 'Web inferred',
  '同名待确认': 'Name match unconfirmed',
  '目标岗位': 'Target role',
  '临时密码': 'Temporary Password',
  '为新账户设置临时密码': 'Set a temporary password for the new account',
  '请为新账户设置临时密码': 'Please set a temporary password for the new account',
  '作品集': 'Portfolio',
  '图片作品集': 'Image portfolio',
  '网页作品集': 'Web portfolio',
  '薪资待定': 'Salary TBC',
  '尚未选择岗位': 'No job selected',
  '更新密码': 'Update Password',
  '修改登录密码': 'Change Sign-in Password',
  '更新后会保留当前设备，并撤销该账户的其他会话。': 'Your current device stays signed in; all other sessions for this account are revoked.',
  '密码已更新，其他登录会话已撤销': 'Password updated; other sessions were revoked',
  '无法更新密码': 'Could not update the password',
  '姓名不能为空。': 'Name cannot be empty.',
  '仅支持 PNG、JPG、WEBP、GIF 格式图片。': 'Only PNG, JPG, WEBP, and GIF images are supported.',
  '，并已进入评估队列': ' and entered the assessment queue',
  '份文件名无法识别、需要人工确认姓名。': 'filenames could not be matched; confirm those names manually.',
};
const DYNAMIC_RULES = [
  [/^正在翻译简历（第 (\d+)\/(\d+) 段）$/, 'Translating the resume (part $1/$2)'],
  [/^年$/, ''],
  [/^· 第 (\d+)\/(\d+) 次$/, '· attempt $1/$2'],
  [/^上次检查：正常$/, 'Last check: OK'],
  [/^上次检查：失败$/, 'Last check: failed'],
  [/^(\d+) 份文件名无法识别、需要人工确认姓名。$/, '$1 filenames could not be matched; confirm those names manually.'],
  [new RegExp('^份材料$'), 'files'],
  [new RegExp('^份材料\\ ·$'), 'files ·'],
  [new RegExp('^份简历$'), 'resumes'],
  [new RegExp('^份简历，$'), 'resumes, '],
  [new RegExp('^份待处理$'), 'pending'],
  [new RegExp('^份资产$'), 'assets'],
  [new RegExp('^条证据$'), 'evidence items'],
  [new RegExp('^条证据\\ ·\\ 置信度$'), 'evidence items · Confidence'],
  [new RegExp('^个岗位\\ ·$'), 'jobs ·'],
  [new RegExp('^位成员$'), 'members'],
  [new RegExp('^位授权成员$'), 'authorized members'],
  [new RegExp('^个岗位画像$'), 'job profiles'],
  [new RegExp('^个标准画像$'), 'standard profiles'],
  [new RegExp('^项待处理$'), 'pending'],
  [new RegExp('^位候选人$'), 'candidates'],
  [new RegExp('^位候选人\\ ·$'), 'candidates ·'],
  [new RegExp('^位候选人，自动归档$'), 'candidates, auto-archived'],
  [new RegExp('^个可访问岗位$'), 'accessible jobs'],
  [new RegExp('^人$'), 'people'],
  [new RegExp('^人待复核$'), 'to review'],
  [new RegExp('^年$'), 'years'],
  [new RegExp('^年明确职业跨度$'), 'years of documented career span'],
  [new RegExp('^最早记录始于$'), 'Earliest record from'],
  [new RegExp('^另有$'), 'Plus'],
  [new RegExp('^项，建议在面试前统一确认。$'), 'more items; verify them before the interview.'],
  [new RegExp('^显示$'), 'Showing'],
  [new RegExp('^已选择$'), 'Selected'],
  [new RegExp('^个字段$'), 'fields'],
  [new RegExp('^类信号明确禁用$'), 'signal categories explicitly blocked'],
  [new RegExp('^·\\ 第$'), '· attempt'],
  [new RegExp('^已忽略：$'), 'Excluded: '],
  [new RegExp('^流程已关闭\\ ·$'), 'Workflow closed ·'],
  [new RegExp('^个通知/日历任务待\\ Graph\\ 配置$'), 'notification/calendar tasks awaiting Graph setup'],
  [new RegExp('^候选人识别预览\\ ·$'), 'Candidate matching preview ·'],
  [new RegExp('^·\\ 文件名识别置信度$'), '· filename match confidence'],
  [new RegExp('^识别$'), 'matched'],
  [new RegExp('^版本$'), 'version'],
  [/^置信度 高$/, 'Confidence High'],
  [/^置信度 中$/, 'Confidence Medium'],
  [/^置信度 低$/, 'Confidence Low'],
  [/^AI 置信度 高$/, 'AI confidence High'],
  [/^AI 置信度 中$/, 'AI confidence Medium'],
  [/^AI 置信度 低$/, 'AI confidence Low'],
  [/^(\d+) 个可访问岗位 · (\d+) 位候选人$/, '$1 accessible jobs · $2 candidates'],
  [/^(\d+) 个岗位 · (\d+) 个招聘中$/, '$1 jobs · $2 active'],
  [/^(\d+) 个岗位 · 更新于 (.+)$/, '$1 jobs · Updated $2'],
  [/^(\d+) 个负责岗位 · 优先处理复核与面试反馈$/, '$1 assigned jobs · Prioritize reviews and interview feedback'],
  [/^(\d+) 位候选人尚未完成 AI 评估$/, '$1 candidates have not completed AI assessment'],
  [/^(\d+) 位候选人$/, '$1 candidates'],
  [/^(\d+) 位候选人的人工流程状态$/, 'Human workflow status for $1 candidates'],
  [/^(\d+) 位候选人已进入 AI 评估队列$/, '$1 candidates added to the AI assessment queue'],
  [/^(\d+) 人已完成 AI 评估$/, '$1 AI assessments completed'],
  [/^(\d+) 人已推荐面试$/, '$1 recommended for interview'],
  [/^(\d+) 场已确认$/, '$1 confirmed'],
  [/^(\d+) 项待处理$/, '$1 pending'],
  [/^(\d+) 个任务正在运行$/, '$1 tasks running'],
  [/^(\d+) 个岗位画像$/, '$1 job profiles'],
  [/^(\d+) 个标准画像$/, '$1 standard profiles'],
  [/^(\d+) 个内部校准样本$/, '$1 internal calibration samples'],
  [/^(\d+) 人$/, '$1 people'],
  [/^(\d+) 份材料$/, '$1 files'],
  [/^(\d+) 条证据$/, '$1 evidence items'],
  [/^显示 (\d+) \/ (\d+) 位候选人$/, 'Showing $1 / $2 candidates'],
  [/^已选择 (\d+) 人$/, '$1 selected'],
  [/^已批量更新 (\d+) 位候选人为“备选”$/, 'Updated $1 candidates to “Shortlist”'],
  [/^已批量更新 (\d+) 位候选人为“推荐面试”$/, 'Updated $1 candidates to “Recommend Interview”'],
  [/^已批量更新 (\d+) 位候选人为“不匹配”$/, 'Updated $1 candidates to “Not a Match”'],
  [/^已删除 (\d+) 位候选人$/, 'Deleted $1 candidates'],
  [/^确认删除 (\d+) 位候选人的全部数据$/, 'Delete all data for $1 candidates?'],
  [/^确认将 (\d+) 位候选人设为“不匹配”$/, 'Mark $1 candidates as “Not a Match”?'],
  [/^选择 (.+)$/, 'Select $1'],
  [/^(.+) 已删除$/, '$1 deleted'],
  [/^(\d+) 份文件 · 识别到约 (\d+) 位候选人 · (.+)$/, '$1 files · Approximately $2 candidates · $3'],
  [/^候选人识别预览 · (\d+) 份简历$/, 'Candidate detection preview · $1 resumes'],
  [/^(.+) 已归档到 (.+)，并已进入评估队列$/, '$1 archived under $2 and queued for assessment'],
  [/^(.+) 已归档到 (.+)$/, '$1 archived under $2'],
  [/^，(\d+) 份待匹配$/, ', $1 unmatched'],
  [/^权重 (\d+(?:\.\d+)?)% · (\d+) 条证据$/, 'Weight $1% · $2 evidence items'],
  [/^(\d+) 分$/, '$1 points'],
  [/^(\d+) 年明确职业跨度$/, '$1 years of verified career history'],
  [/^最早记录始于 (\d{4}) 年$/, 'Earliest record: $1'],
  [/^(\d+) 分钟$/, '$1 minutes'],
  [/^(\d+) 个通知\/日历任务待 Graph 配置$/, '$1 notification/calendar tasks awaiting Graph configuration'],
  [/^(\d+) 个岗位 · (\d+) 位授权成员$/, '$1 jobs · $2 authorized members'],
  [/^翻译为(.+)$/, 'Translate to $1'],
  [/^翻译已生成：(.+) → (.+)$/, 'Translation generated: $1 → $2'],
  [/^招聘阶段已更新为“(.+)”(.*)$/, 'Recruiting stage updated to “$1”$2'],
  [/^该专业与(.+)的核心能力直接相关$/, 'This major is directly relevant to the core capabilities required for $1'],
  [/^视觉模型复读 (\d+) 个扫描或低覆盖页面$/, 'Vision model rereading $1 scanned or low-coverage pages'],
  [/^正在核验公司背景：(.+)$/, 'Researching company background: $1'],
  [/^(.+)：已返回$/, '$1 responded'],
  [/^(.+)：等待搜索模型响应$/, 'Waiting for the search model for $1'],
  [/^(.+)：正在搜索并整理来源$/, 'Searching and organizing sources for $1'],
  [/^已完成 (\d+) 家公司的背景核验$/, 'Completed research for $1 companies'],
  [/^主模型失败，正在切换 Fallback：(.+)$/, 'Primary model failed; switching to fallback: $1'],
  [/^正在调用 (.+)（第 (\d+)\/(\d+) 次）$/, 'Calling $1 (attempt $2 of $3)'],
  [/^正在调用 (.+)$/, 'Calling $1'],
  [/^等待 (.+) 响应（第 (\d+)\/(\d+) 次），已等待 (\d+) 秒，已持续接收输出 (\d+) 秒$/, 'Waiting for $1 (attempt $2 of $3) · $4 seconds elapsed · receiving output for $5 seconds'],
  [/^等待 (.+) 响应（第 (\d+)\/(\d+) 次），已等待 (\d+) 秒，等待首字节$/, 'Waiting for $1 (attempt $2 of $3) · $4 seconds elapsed · waiting for first byte'],
  [/^等待 (.+) 响应，已等待 (\d+) 秒，已持续接收输出 (\d+) 秒$/, 'Waiting for $1 · $2 seconds elapsed · receiving output for $3 seconds'],
  [/^等待 (.+) 响应，已等待 (\d+) 秒，等待首字节$/, 'Waiting for $1 · $2 seconds elapsed · waiting for first byte'],
  [/^等待 (.+) 响应，已等待 (\d+) 秒(.*)$/, 'Waiting for $1 · $2 seconds elapsed$3'],
  [/^(.+) 不支持流式响应，切换兼容模式重试$/, '$1 does not support streaming; retrying in compatibility mode'],
  [/^(.+)失败，正在重试：(.+)$/, '$1 failed; retrying: $2'],
  [/^(.+)失败：(.+)$/, '$1 failed: $2'],
  [/^(.+) 已返回，正在保存评分结果$/, '$1 responded; saving scoring results'],
  [/^请求失败 \((\d+)\)$/, 'Request failed ($1)'],
  [/^评分指标 (\d+)$/, 'Scoring criterion $1'],
  [/^评分权重合计需为 100%，当前为 (\d+(?:\.\d+)?)%$/, 'Scoring weights must total 100%; current total is $1%'],
  [/^删除部门“(.+)”？$/, 'Delete department “$1”?'],
  [/^置信度 (.+)$/, 'Confidence $1'],
  [/^AI 置信度 (.+)$/, 'AI confidence $1'],
  [/^当前合计 (.+)$/, 'Current total: $1'],
  [/^已保存 · (.+)$/, 'Saved · $1'],
  [/^另有 (\d+) 项，建议在面试前统一确认。$/, '$1 more items; verify them before the interview.'],
  [/^(.+) 已更新为“(.+)”$/, '$1 updated to “$2”'],
  [/^删除候选人 · (.+)$/, 'Delete Candidate · $1'],
  [/^登记面试 · (.+)$/, 'Schedule Interview · $1'],
  [/^人工校准 · (.+)$/, 'Human Calibration · $1'],
  [/^编辑校准 · (.+)$/, 'Edit Calibration · $1'],
  [/^确定删除账户“(.+)”吗？该账户的登录会话和部门授权会立即撤销。$/, 'Delete account “$1”? Its sessions and department access will be revoked immediately.'],
  [/^确定删除 (.+) 的这条校准评价吗？$/, 'Delete this calibration review for $1?'],
  [/^删除岗位“(.+)”及其候选人数据？此操作不可撤销。$/, 'Delete job “$1” and its candidate data? This cannot be undone.'],
  [/^删除模型 (.+)？$/, 'Delete model $1?'],
  [/^删除 (.+)？$/, 'Delete $1?'],
  [/^删除(.+)$/, 'Delete $1'],
];

export function translateUiText(value, locale = DEFAULT_LOCALE) {
  const source = String(value ?? '');
  if (locale !== 'en' || !source) return source;
  const core = source.trim();
  if (!core) return source;
  const leading = source.slice(0, source.indexOf(core));
  const trailing = source.slice(source.indexOf(core) + core.length);
  const wrap = (translated) => `${leading}${translated}${trailing}`;
  if (Object.hasOwn(EN, core)) return wrap(EN[core]);
  for (const [pattern, replacement] of DYNAMIC_RULES) {
    if (pattern.test(core)) return wrap(core.replace(pattern, replacement));
  }
  return source;
}

const textRecords = new WeakMap();
const attributeRecords = new WeakMap();
const ATTRIBUTES = ['placeholder', 'title', 'aria-label'];

function ignored(node) {
  const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  return !element || Boolean(element.closest('[data-i18n-ignore], .resume-document-body'));
}

function localizeTextNode(node, locale) {
  if (ignored(node)) return;
  const current = node.data;
  let record = textRecords.get(node);
  if (locale === 'en') {
    if (!record || current !== record.en) {
      record = { zh: current, en: translateUiText(current, 'en') };
      textRecords.set(node, record);
    }
    if (node.data !== record.en) node.data = record.en;
  } else if (record && node.data !== record.zh) {
    node.data = record.zh;
  }
}

function localizeElement(element, locale) {
  if (ignored(element)) return;
  let records = attributeRecords.get(element);
  if (!records) {
    records = new Map();
    attributeRecords.set(element, records);
  }
  for (const attribute of ATTRIBUTES) {
    if (!element.hasAttribute(attribute)) continue;
    const current = element.getAttribute(attribute) || '';
    let record = records.get(attribute);
    if (locale === 'en') {
      if (!record || current !== record.en) {
        record = { zh: current, en: translateUiText(current, 'en') };
        records.set(attribute, record);
      }
      if (current !== record.en) element.setAttribute(attribute, record.en);
    } else if (record && current !== record.zh) {
      element.setAttribute(attribute, record.zh);
    }
  }
}

function localizeTree(root, locale) {
  if (!root || ignored(root)) return;
  if (root.nodeType === Node.TEXT_NODE) {
    localizeTextNode(root, locale);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  localizeElement(root, locale);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) localizeTextNode(node, locale);
    else localizeElement(node, locale);
    node = walker.nextNode();
  }
}

// The resume body is deliberately excluded from the DOM localizer, so anything
// rendered inside it has to resolve its own language rather than wait to be
// translated in place.
let ACTIVE_LOCALE = DEFAULT_LOCALE;

export function activeLocale() {
  return ACTIVE_LOCALE;
}

export function useUiLocalization(locale, rootId = 'root') {
  ACTIVE_LOCALE = locale;
  useLayoutEffect(() => {
    const root = document.getElementById(rootId);
    if (!root) return undefined;
    document.documentElement.lang = locale;
    document.title = locale === 'en' ? 'Talent Intelligence · Recruiting Workspace' : 'Talent Intelligence · 简历筛选工作台';
    localizeTree(root, locale);

    let scheduled = false;
    const pending = new Set();
    const flush = () => {
      scheduled = false;
      for (const node of pending) localizeTree(node, locale);
      pending.clear();
    };
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') mutation.addedNodes.forEach((node) => pending.add(node));
        else pending.add(mutation.target);
      }
      if (!scheduled) {
        scheduled = true;
        queueMicrotask(flush);
      }
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRIBUTES });

    const nativeConfirm = window.confirm.bind(window);
    const nativeAlert = window.alert.bind(window);
    window.confirm = (message) => nativeConfirm(translateUiText(message, locale));
    window.alert = (message) => nativeAlert(translateUiText(message, locale));
    window.__talentLocale = locale;
    window.__t = (message) => translateUiText(message, locale);
    return () => {
      observer.disconnect();
      window.confirm = nativeConfirm;
      window.alert = nativeAlert;
    };
  }, [locale, rootId]);
}
